// Root component — owns auth state, workspace selection, all top-level handlers,
// and the AuthContext value. Renders AppRouter for routing.

import React from 'react';
import * as Sentry from '@sentry/react';
import buildInfo from './buildInfo.json';
import { useLocation, useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useWorkspaceDefaultsHandler } from './hooks/useWorkspaceDefaultsHandler';
import { useEmailTemplateHandlers } from './hooks/useEmailTemplateHandlers';
import { useExpenseHandlers } from './hooks/useExpenseHandlers';
import { useIncomeHandlers } from './hooks/useIncomeHandlers';
import { useCombinedTemplateFolders } from './hooks/useCombinedTemplateFolders';
import { useTransactionHandlers } from './hooks/useTransactionHandlers';
import { useVendorHandlers } from './hooks/useVendorHandlers';
import { useCompanyListHandlers } from './hooks/useCompanyListHandlers';
import { useStatusHandlers } from './hooks/useStatusHandlers';
import { useWorkspaceHandlers } from './hooks/useWorkspaceHandlers';
import { useContactHandlers } from './hooks/useContactHandlers';
import { useContactsMigration } from './hooks/useContactsMigration';
import useGoogleOAuthStatus from './hooks/useGoogleOAuthStatus';
import * as workspaceService from './services/workspaceService';
import * as transactionService from './services/transactionService';
import * as invitationService from './services/invitationService';

import { auth, db } from './firebase';
import Login from './components/auth/Login';
import PrivacyPolicy from './components/auth/PrivacyPolicy';
import TermsOfService from './components/auth/TermsOfService';
import ProtectedRoute, { returnToFromState } from './components/ProtectedRoute';
import Sidebar from './components/ui/Sidebar';
import Spinner from './components/ui/primitives/Spinner';
import ClientPortalPage from './components/features/portal/ClientPortalPage';
import AcceptInvitationPage from './components/features/invitations/AcceptInvitationPage';
import WelcomePage from './components/features/invitations/WelcomePage';
import PlanSelection from './components/features/signup/PlanSelection';
import SignupRedirect from './components/features/signup/SignupRedirect';
import MessagingOnboardingBanner from './components/features/messaging/MessagingOnboardingBanner';
import VersionBanner, { versionModeForPath, QuietReloadWatcher, useStashIdentitySync } from './components/ui/VersionBanner';
import AccountStatusBanner from './components/features/account/AccountStatusBanner';
import BillingLifecycleStrip from './components/features/billing/BillingLifecycleStrip';
import { setWorkspaceWriteAccess, deriveWriteAccess, writeErrorMessage } from './services/firestoreWrites';
import AccountDeletedScreen from './components/features/account/AccountDeletedScreen';
import { ToastStack } from './components/ui/Toast';
import { useToast } from './contexts/ToastContext';
import { BrokerageProvider } from './contexts/BrokerageContext';
import { normalizeBrokerage } from './commissions/brokerage';
import { buildPricingSettings } from './commissions/pricing';
import { deriveFinancialFieldsForModule } from './commissions';
import { AuthProvider } from './contexts/AuthContext';
import { MobileHeaderActionsProvider, MobileHeaderActionsSlot } from './contexts/MobileHeaderActionsContext';
import CreateWorkspaceModal from './components/ui/modals/CreateWorkspaceModal';
import DeleteWorkspaceModal from './components/ui/modals/DeleteWorkspaceModal';
import { deleteBlockers } from './utils/workspaceDeleteBlockers';
import MoveTransactionModal from './components/ui/modals/MoveTransactionModal';
import OnboardingWizard from './components/auth/OnboardingWizard';
import { postOnboardingLanding, resolveOnboardingRole } from './components/auth/onboardingLanding';
import { TourProvider, useTour } from './components/features/tours/TourContext';
import { useIsTourRunning } from './components/features/tours/tourRunningStore';
import { nudgeFitsLocation } from './components/features/tours/tourRoute';
import WelcomeTourLauncher from './components/features/tours/WelcomeTourLauncher';
import TourLaunchIntent from './components/features/tours/TourLaunchIntent';
import { KeyFlowAiProvider } from './components/features/keyflowAi/KeyFlowAiContext';
import KeyFlowAiSurfaces from './components/features/keyflowAi/KeyFlowAiSurfaces';
import NudgePopover from './components/ui/NudgePopover';
import AnnouncementPopup from './components/ui/AnnouncementPopup';
import { useAnnouncements } from './hooks/useAnnouncements';
import { useTourNudge } from './hooks/useTourNudge';
import TcModeOverlay from './components/ui/overlays/TcModeOverlay';
import RouteSkeleton from './components/ui/skeletons/RouteSkeleton';
import RouteChunkErrorBoundary from './components/ui/RouteChunkErrorBoundary';
import { prefetchTopRoutes } from './utils/prefetchRoutes';
// App navigation outside a page asks the page's unsaved-changes guard first.
import { requestNavigate } from './navigation/leaveGuardRegistry';
import { AppRouter } from './components/AppRouter';
import ContractExtractionNotifier from './components/features/transactions/ContractExtractionNotifier';
import {
    normalizeTemplateTargetClients,
} from './utils/emailTemplates';
import { DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS, sanitizeWorkspaceDataEntrySettings, DEFAULT_EXP_MODULE_SETTINGS, sanitizeExpModuleSettings } from './config/workspaceDefaults';
import { PUBLIC_ROUTE_PATHS, isPublicRoutePath } from './config/publicRoutes';
import { ensureUserProfileDocument } from './services/userProfile';
import { LIGHT_THEMES, DARK_THEMES, DEFAULT_LIGHT_THEME, DEFAULT_DARK_THEME } from './themeConfig';
import useMediaQuery from './hooks/useMediaQuery';
import { useUserSettings } from './hooks/useUserSettings';
import { useWorkspaceData } from './hooks/useWorkspaceData';
import { useWorkspaceList } from './hooks/useWorkspaceList';
import { useTaws } from './hooks/useTaws';
import { useTawTransactions } from './store/hooks/useTawTransactions';
import { ARCHIVE_YEAR_ALL, computeTawScope, computeEffectiveTeamScope } from './utils/tawArchiveScope';
import { useAsyncHandler } from './hooks/useAsyncHandler';
import { getViewTitle, PATH_FOR_VIEW, getViewFromPathname, canAccessManageWorkspaceFor } from './components/ui/navigationConfig';
import { useLeasedCollection, useLeasedCollectionLoaded } from './store/hooks/useLeasedCollection';
import { useTransactions, useTransactionsLoaded } from './store/hooks/useTransactions';
import { usePendingBrokerageResync, useRunPendingBrokerageResync } from './hooks/usePendingBrokerageResync';
import { dropForeignWorkspace } from './store/workspaceStore';
import { useLenders } from './store/hooks/useLenders';
import { useTitleCompanies } from './store/hooks/useTitleCompanies';
import { useRealtors } from './store/hooks/useRealtors';
import { useEmailTemplatesLease } from './store/hooks/useEmailTemplatesLease';
import { useMemberProfiles } from './store/hooks/useMemberProfiles';

// 44-08 (D-02, D-04): the /help and /help/:slug lazy chunks. This file's
// FIRST React.lazy() declarations — the six existing lazy routes all live in
// AppRouter.js, under the authenticated shell. Two separate chunks, not one:
// the index page never needs TOC/related-articles/failure-state code, so
// splitting keeps its chunk minimal (44-UI-SPEC.md Section 8).
const HelpIndexPage = React.lazy(() => import('./components/features/help/HelpIndexPage'));
const HelpArticlePage = React.lazy(() => import('./components/features/help/HelpArticlePage'));

// Phase 68 (D-14): the four public lead pages. ONE module, so the pages,
// publicFirebase, and the App Check SDK are a single lazy chunk that no
// signed-in session ever downloads. Each route element is a named export.
const lazyLeadRoute = (name) => React.lazy(
    () => import('./components/features/leads/public/LeadPublicRoutes').then((m) => ({ default: m[name] }))
);
const LeadFormRoute = lazyLeadRoute('LeadFormRoute');
const OpenHouseRoute = lazyLeadRoute('OpenHouseRoute');
const KioskRoute = lazyLeadRoute('KioskRoute');
const ConfirmRoute = lazyLeadRoute('ConfirmRoute');

function PublicRouteFallback() {
    return (
        <div className="min-h-screen flex items-center justify-center text-[color:var(--color-primary)]">
            <Spinner size="md" />
        </div>
    );
}

const createGoogleProvider = () => new GoogleAuthProvider();

// 40-10: shared by the top-level uidsNeeded computation below and the
// member-fetch effect, so the workspace's member-id set and directory are
// derived from selectedWorkspace exactly once per call site instead of
// twice. Plain function (not a hook) -- safe to call from both a useMemo
// and inside a useEffect body.
function computeWorkspaceMemberIdsAndDirectory(workspace) {
    const memberIdSet = new Set();
    if (Array.isArray(workspace?.members)) {
        workspace.members.forEach(memberId => {
            if (typeof memberId === 'string' && memberId.trim()) {
                memberIdSet.add(memberId.trim());
            }
        });
    }
    if (workspace?.ownerId && typeof workspace.ownerId === 'string') {
        const normalizedOwnerId = workspace.ownerId.trim();
        if (normalizedOwnerId) {
            memberIdSet.add(normalizedOwnerId);
        }
    }
    const memberDirectory = (workspace?.memberDirectory && typeof workspace.memberDirectory === 'object')
        ? workspace.memberDirectory
        : {};
    return { memberIds: Array.from(memberIdSet), memberDirectory };
}

// D-03/D-04 (Plan 36-03): the three data-heavy routes that get the eager
// RouteSkeleton fallback while their lazy chunk downloads. Kanban (also
// currentView === 'transactions') is deliberately excluded — it keeps the
// spinner (see showSkeleton below), so this set alone is not sufficient to
// decide the fallback for the transactions view.
const SKELETON_VIEWS = new Set(['dashboard', 'transactions', 'contacts']);

// D-20 (39-17, Task 1): App.js's write-path handlers (useExpenseHandlers, useIncomeHandlers, useTransactionHandlers' counter
// sync, the one-time company-migration effect) still need these collections
// -- but gated by currentView so each lease stays genuinely per-route
// (opens only while a route that could exercise the write path is active),
// not a disguised boot listener. Route sets are deliberately generous
// (covering every route that could invoke the dependent handler), not
// minimal-per-consumer -- see the phase's per-route lease discipline (D-20).
const VENDOR_DATA_VIEWS = new Set(['businessPartners', 'manageWorkspace', 'form', 'contacts']);
const EMAIL_ASSET_VIEWS = new Set(['contacts', 'form']);
const EXPENSE_TYPE_VIEWS = new Set(['expenses', 'form', 'settings']);
const INCOME_TYPE_VIEWS = new Set(['income', 'settings']);
const TRANSACTION_WRITE_VIEWS = new Set(['transactions', 'form']);

// DATA-01/DATA-04 (39-22, Task 2): useWorkspaceData.js's `transactions` and
// `contacts` boot listeners are gone -- this file's own write-path handlers
// that still need them (useExpenseHandlers, useIncomeHandlers,
// useTransactionHandlers,
// useContactHandlers, the one-shot contacts-migration effect, the
// selectedTransaction refresh effect) now read gated on-demand leases
// instead, following the exact same currentView-gating discipline as the
// D-20 collections above. This single generous union covers every view any
// of those handlers could fire from (mirrors 39-17's own "deliberately
// generous, not minimal-per-consumer" precedent) -- 'dashboard' is
// deliberately excluded, so a plain boot never opens either lease.
// DATA-05 (39): 'settings' is DELIBERATELY EXCLUDED. App holds this
// transactions lease at the ROOT to feed its write-path handlers, so any view
// in this set makes a transaction edit re-render the entire App tree. The
// settings route has no App-level transactions consumer (its only transactions
// need is DataTab's export, which now leases its own at the leaf), so including
// it here re-rendered the whole Settings tree on every transaction change for
// no reason -- the DATA-05 violation caught in the UAT profiler. Settings now
// behaves like the other non-transaction views (dashboard, etc.): App simply
// does not lease transactions while it is the active view.
const TRANSACTION_DATA_VIEWS = new Set([
    'transactions', 'form', 'expenses', 'income',
    'contacts', 'businessPartners', 'manageWorkspace',
]);

// AppNudge renders inside the TourProvider tree so useTourNudge and useTour are available.
// Dismiss ("Don't show this again") writes 'dismissed' durably per tour through
// useTourNudge's dismiss(). Start launches the tour and raises a toast when the
// launch cannot proceed. The tour's own lifecycle owns the completed-or-skipped
// write: onStart does NOT call dismiss(), and does not set any local hide flag,
// on a successful launch. The popover disappears on its own because
// isNudgeSuppressed recomputes reactively off the live tourState snapshot once
// onDestroyStarted writes 'completed' or 'skipped' (or writes nothing at all, on
// step 1) — a local hide-and-write here would defeat D-06's rule that closing on
// step 1 must nudge again.
function AppNudge() {
    const { tourId, nudgeBody, visible, dismiss } = useTourNudge();
    const { launchTour } = useTour();
    const { showToast } = useToast();
    const location = useLocation();

    if (!visible || !tourId) return null;
    // C-WR-06: on /templates useTourNudge picks the tour for the tab on screen
    // (Email or Checklists; Action plans has none). This guard is the backstop:
    // a nudge that would switch tabs on accept (and, on Action plans, could drop
    // unsaved plan edits) never shows.
    if (!nudgeFitsLocation(tourId, location)) return null;

    const onStart = async () => {
        const result = await launchTour(tourId);
        if (!result.ok) {
            showToast(result.hint, 'error');
        }
    };

    return (
        <NudgePopover
            body={nudgeBody}
            onStart={onStart}
            onDismiss={dismiss}
        />
    );
}

function ClientPortalRouteWrapper() {
    const { token } = useParams();
    // Portal is shown to external clients via a public link. Lock to KeyFlow Light
    // regardless of the agent's chosen theme so every client sees a consistent brand.
    const portalThemeStyle = { ...LIGHT_THEMES.keyflow.variables, colorScheme: 'light' };
    return (
        <div style={portalThemeStyle} className="min-h-screen bg-[color:var(--body-bg)] text-[color:var(--text-primary)]">
            <ClientPortalPage token={(token || '').trim()} />
        </div>
    );
}

function App() {
    const location = useLocation();
    const rrNavigate = useNavigate();
    const currentView = getViewFromPathname(location.pathname);
    const transactionView =
        location.pathname === '/transactions/kanban' ? 'kanban'
        : location.pathname === '/transactions/archive' ? 'archive'
        : 'list';
    const [user, setUser] = React.useState(null);
    const { workspaces, allWorkspaces, selectedWorkspaceId, setSelectedWorkspaceId, updateSelectedWorkspace, setExtraWorkspaces } = useWorkspaceList(user);
    useStashIdentitySync({ uid: user?.uid || null, workspaceId: selectedWorkspaceId || null });
    const [emailTemplateFolders, setEmailTemplateFolders] = React.useState([]);
    const [workspaceDataEntrySettings, setWorkspaceDataEntrySettings] = React.useState(DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS);
    const [expModuleSettings, setExpModuleSettings] = React.useState(DEFAULT_EXP_MODULE_SETTINGS);
    const [selectedTransaction, setSelectedTransaction] = React.useState(null);
    const [isCreateWorkspaceModalOpen, setCreateWorkspaceModalOpen] = React.useState(false);
    const [isDeleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] = React.useState(false);
    const [isMoveTransactionModalOpen, setMoveTransactionModalOpen] = React.useState(false);
    const [transactionToMove, setTransactionToMove] = React.useState(null);
    const [isStatusManagerOpen, setIsStatusManagerOpen] = React.useState(false);
    const [workspaceMembers, setWorkspaceMembers] = React.useState([]);
    const { showToast, dismissToast, toasts } = useToast();
    const { run } = useAsyncHandler();
    // MEAS-02 (Phase 35, D-05/D-06): boot -> first-meaningful-paint timing refs.
    // authResolvedRef flips true the first time onAuthStateChanged fires (even
    // with a null user, i.e. signed-out). bootFmpMarkedRef guards the boot-fmp
    // mark + span attach so it only ever fires once per page load.
    const authResolvedRef = React.useRef(false);
    const bootFmpMarkedRef = React.useRef(false);
    const idlePrefetchRanRef = React.useRef(false);
    // WR-01: authResolved is a STATE mirror of authResolvedRef. The ref alone
    // cannot drive the boot-fmp effect for a signed-out session: setUser(null)
    // on already-null state does not re-render, so an effect keyed only on
    // [user] never re-runs and boot-duration is never recorded. Flipping this
    // state flag false -> true on auth resolution guarantees exactly one
    // re-render (and effect run) regardless of whether a user signed in.
    const [authResolved, setAuthResolved] = React.useState(false);
    // Task 7: the signed-in user's OWN account status, from ensureUserAccount
    // -- the first callable the client makes after sign-in (D-10). null means
    // "not yet known" (never treated as deleted); an absent `status` from the
    // server reads as 'active' (functions/userAccount.js).
    const [accountStatus, setAccountStatus] = React.useState(null);
    const [accountPurgeAfter, setAccountPurgeAfter] = React.useState(null);
    // The admin-authored explanation, captioned "Shown to the customer on
    // their account page" in the admin confirm step. Rendered by the
    // suspension banner and the deleted-account screen.
    const [accountStatusReason, setAccountStatusReason] = React.useState(null);
    // True when this user's own account is fine but every workspace they own
    // was cascade-deleted with their payer's account. The rules exclude a
    // deleted workspace from `list`, so the client cannot see this for
    // itself -- functions/userAccount.js reports it.
    const [accountCascadeDeleted, setAccountCascadeDeleted] = React.useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
    // KeyFlowAiSurfaces owns the Help drawer and the KeyFlow AI dock; this ref is
    // how the sidebar build-version link asks it to open Help.
    const openHelpRef = React.useRef(null);
    // D-27: the sidebar build-version link opens Help focused on What's New.
    const [helpFocusWhatsNew, setHelpFocusWhatsNew] = React.useState(false);
    const handleOpenWhatsNew = React.useCallback(() => {
        setHelpFocusWhatsNew(true);
        if (openHelpRef.current) openHelpRef.current();
    }, []);
    // What's New (D-14 to D-19, 54-10-PLAN.md): one hook call feeds both the
    // pop-up (below, near HelpFab) and HelpMenuPanel's permanent list.
    const {
        helpList: announcementHelpList,
        helpPanel: announcementHelpPanel,
        popupAnnouncements,
        hasUnseen: hasUnseenAnnouncements,
        error: announcementsError,
        loading: announcementsLoading,
        markShownSeen: markAnnouncementsShown,
    } = useAnnouncements({ user, workspaceId: selectedWorkspaceId });
    const isTourOverlayActive = useIsTourRunning();
    // SA-01: restartOnboarding — reopens the OnboardingWizard on demand from the Help drawer.
    // forceOpenOnboardingRef lets the wizard gate effect know not to suppress this explicit open.
    const forceOpenOnboardingRef = React.useRef(false);
    // Defensive guard (bug: 2026-09-24-onboarding-wizard-before-plan-choice.md): the
    // wizard's steps are workspace-scoped (updateWorkspaceSettings, oauth.initiate), so
    // replaying it with no selectedWorkspaceId would open a wizard whose writes silently
    // no-op. The Help drawer's "Replay setup" row is disabled for the same reason
    // (HelpMenuPanel's hasWorkspace prop below); this is the belt to that suspenders.
    const handleRestartOnboarding = React.useCallback(() => {
        if (!selectedWorkspaceId) return;
        forceOpenOnboardingRef.current = true;
        setShowOnboardingWizard(true);
    }, [selectedWorkspaceId]);
    const [transactionFormTab, setTransactionFormTab] = React.useState('details');
    const [showOnboardingWizard, setShowOnboardingWizard] = React.useState(false);
    const [hasCompletedOnboardingSession, setHasCompletedOnboardingSession] = React.useState(false);
    const [isTcOverlayVisible, setTcOverlayVisible] = React.useState(false);
    const [expenseModalTrigger, setExpenseModalTrigger] = React.useState(0);
    const [incomeModalTrigger, setIncomeModalTrigger] = React.useState(0);
    const [vendorModalTrigger, _setVendorModalTrigger] = React.useState(0);
    const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);
    const isMobileViewport = useMediaQuery('(max-width: 1023px)');

    const applyThemeVariant = React.useCallback((lightKey, darkKey) => {
        if (typeof document === 'undefined' || typeof window === 'undefined') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const prefersDark = mediaQuery.matches;
        const themeMap = prefersDark ? DARK_THEMES : LIGHT_THEMES;
        const fallbackKey = prefersDark ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
        const themeKey = prefersDark ? darkKey : lightKey;
        const theme = themeMap[themeKey] || themeMap[fallbackKey];
        if (!theme) return;
        Object.entries(theme.variables).forEach(([cssVar, value]) => {
            document.documentElement.style.setProperty(cssVar, value);
        });
        const colorScheme = theme.colorScheme || (prefersDark ? 'dark' : 'light');
        document.documentElement.style.setProperty('color-scheme', colorScheme);
        // Referral badge tokens aren't defined per-theme. Light mode keeps the
        // warm dark-red; dark mode needs LIGHT warm text or it renders red-on-red
        // and is unreadable. A theme may still override by defining the vars.
        if (!theme.variables['--badge-referral-bg']) {
            const isDarkScheme = colorScheme === 'dark';
            document.documentElement.style.setProperty(
                '--badge-referral-bg',
                isDarkScheme ? 'rgba(245, 158, 110, 0.20)' : 'rgba(120, 53, 15, 0.12)'
            );
            document.documentElement.style.setProperty(
                '--badge-referral-text',
                isDarkScheme ? '#F4B183' : '#7C2D12'
            );
        }
    }, []);

    const defaultUserSettings = React.useMemo(() => ({
        firstName: '',
        lastName: '',
        signature: '',
        defaultSignatureId: '',
        lightTheme: DEFAULT_LIGHT_THEME,
        darkTheme: DEFAULT_DARK_THEME,
        role: 'realtor',
        isTcModeEnabled: false,
        onboardingCompleted: false,
        emailTemplatesDefaultSort: 'name',       // V2 template grid default sort: 'name' (alphabetical) or 'recent'
        emailTemplatesDefaultGroup: 'audience',  // V2 template grid default grouping: 'audience' or 'none'
        // Templates -> Marketing emails keeps its own grid defaults (owner decision, 2026-10-15).
        emailMarketingTemplatesDefaultSort: 'name',      // 'name' or 'recent'
        emailMarketingTemplatesDefaultGroup: 'campaign', // 'campaign' or 'none'
        termsAcceptedAt: null,
        tcOverlayDismissed: false,
    }), []);

    const { userSettings, setUserSettings, userSettingsLoaded } = useUserSettings(user, defaultUserSettings);

    React.useEffect(() => {
        applyThemeVariant(userSettings.lightTheme, userSettings.darkTheme);
        if (typeof window === 'undefined') return undefined;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => applyThemeVariant(userSettings.lightTheme, userSettings.darkTheme);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [userSettings.lightTheme, userSettings.darkTheme, applyThemeVariant]);
    const [isSavingSettings, setIsSavingSettings] = React.useState(false);
    const selectedWorkspace = React.useMemo(
        () => allWorkspaces.find(w => w.id === selectedWorkspaceId) || null,
        [allWorkspaces, selectedWorkspaceId]
    );
    const canManageWorkspaceAccess = React.useMemo(() => {
        const ownerId = typeof selectedWorkspace?.ownerId === 'string' ? selectedWorkspace.ownerId.trim() : '';
        const currentUserId = typeof user?.uid === 'string' ? user.uid.trim() : '';
        return Boolean(ownerId && currentUserId && ownerId === currentUserId);
    }, [selectedWorkspace, user]);

    // Task 7: true when the active workspace is a team-agent (TAW) whose
    // suspension cascaded down from its payer's account (Task 3's cascade
    // marker), rather than a directly suspended account. Feeds
    // AccountStatusBanner's isCascaded prop so the banner's copy blames the
    // team's account, never the agent -- their own account is fine.
    const isAccountStatusCascaded = React.useMemo(() => {
        if (selectedWorkspace?.teamRole !== 'team_agent') return false;
        const cascadeByUid = selectedWorkspace?.cascade?.byUid;
        if (!cascadeByUid) return false;
        return cascadeByUid !== user?.uid;
    }, [selectedWorkspace, user?.uid]);

    // Mirror the active workspace's read-only state into the service-layer
    // write chokepoint (src/services/firestoreWrites.js), so a refused write
    // raises a typed WorkspaceReadOnlyError with copy the user can act on
    // rather than a raw permission-denied that surfaces as nothing at all.
    //
    // A module-level setter rather than context because services are plain
    // modules with no access to React state, and threading a flag through 114
    // write call sites is exactly the invasiveness the chokepoint avoids.
    React.useEffect(() => {
        // isAccountStatusCascaded rides along so a refused write says the same
        // thing the banner above it says. Without it the toast blamed the team
        // agent's own account, which is fine, while the banner correctly
        // blamed the team's -- two contradictory claims on one screen.
        setWorkspaceWriteAccess(
            deriveWriteAccess(selectedWorkspace?.status, accountStatusReason, isAccountStatusCascaded)
        );
    }, [selectedWorkspace?.status, accountStatusReason, isAccountStatusCascaded]);

    // Keep the account status LIVE for the current session.
    //
    // ensureUserAccount only runs at sign-in and on navigation, so an admin
    // suspending or deleting a customer mid-session left the app stale in the
    // worst possible way: the workspace vanished from the switcher immediately
    // (that list is snapshot-driven) while the deleted screen stayed unarmed,
    // leaving an empty app with no explanation — the exact state the deleted
    // screen exists to prevent. An admin acting on a live session is the normal
    // case, not an edge case.
    //
    // Gated on accountStatus having already resolved so this attaches AFTER
    // boot: DATA-01's boot-listener budget is met exactly, and a fifth boot
    // listener would breach it. userAccount/{uid} is owner-readable
    // (firestore.rules:182), so no rules change is needed.
    const accountStatusResolved = accountStatus !== null;
    React.useEffect(() => {
        if (!user?.uid || !accountStatusResolved) return undefined;
        return onSnapshot(
            doc(db, 'userAccount', user.uid),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                // Absent status reads as active — there is no backfill migration.
                setAccountStatus(data.status || 'active');
                setAccountStatusReason(data.statusReason || null);
                setAccountPurgeAfter(data.purgeAfter || null);
            },
            (err) => {
                // Never blank the app on a listener failure: the last known
                // status stays in place and the next navigation re-fetches.
                console.warn('userAccount status listener failed:', err);
            }
        );
    }, [user?.uid, accountStatusResolved]);

    // D-02: relaxed from owner-only to owner-OR-member, mirroring
    // hasWorkspaceAccess's three-way predicate (firestore.rules:186-199) —
    // ownerId equality, OR members array-contains, OR a truthy
    // memberDirectory[uid] entry. The client gate must never be broader than
    // the rules gate it mirrors (T-41-04-EOP): real enforcement stays in the
    // rules layer, this boolean only decides which rows App.js composes for
    // display.
    const isParentWorkspaceOwner = React.useMemo(() => {
        if (selectedWorkspace?.teamRole !== 'team') return false;
        const uid = user?.uid;
        if (!uid) return false;
        return (
            selectedWorkspace.ownerId === uid
            || (Array.isArray(selectedWorkspace.members) && selectedWorkspace.members.includes(uid))
            || Boolean(selectedWorkspace.memberDirectory?.[uid])
        );
    }, [selectedWorkspace, user]);

    // Workspace is visible to every member of every workspace, including a
    // Team Agent Workspace (owner decision, 2026-09-25, plan 53-14) — see
    // canAccessManageWorkspaceFor in navigationConfig.js. The narrower
    // question of which TABS that page shows (Members/Billing stay the
    // payer's, D-08) lives in ManageWorkspacePage/planLimits.js's
    // canManageWorkspaceBilling, not here. isParentWorkspaceOwner is a
    // different, narrower question still (owner-or-member of a Team parent)
    // and is now owner-or-member too (D-02).
    const canAccessManageWorkspace = React.useMemo(
        () => canAccessManageWorkspaceFor(selectedWorkspace),
        [selectedWorkspace]
    );

    // TW owner or member: TAW list + team scope picker state + TAW transaction
    // subscriptions. useTaws no longer opens a listener (D-03 superseded —
    // see 41-04-SUMMARY.md): it resolves TAW ids from selectedWorkspace's own
    // teamAgentWorkspaceIds field (already live via the boot workspaces or()
    // listener, no extra subscription needed) and get()s each TAW doc
    // directly. Cost is N one-time reads per TW visit, not a standing
    // listener. NOTE (T-41-04-STALE-GET): teamAgentWorkspaceIds holds TAW
    // WORKSPACE DOCUMENT ids, NOT agent user uids -- that is the DIFFERENT,
    // parallel teamAgentIds field firestore.rules reads directly. Passing
    // teamAgentIds here would silently blank the roster; see
    // src/hooks/useTaws.js's header comment.
    // T-41-CHIPS-STALE (gap-fix, found during live dev verification): this
    // USED to be a SECOND, INDEPENDENT useTaws() instance from
    // ManageWorkspacePage.js's own call -- two separate resolved rosters for the
    // same (twId, teamAgentWorkspaceIds) input. A rename/split/policy edit
    // patched ONLY ManageWorkspacePage's instance (T-41-STALE-ROSTER above), so
    // this instance -- the one that feeds AppRouter's childWorkspaces prop,
    // and therefore the transaction-list scope chips (TeamScopeFilter) --
    // stayed stale until the next twId/idsKey change or a hard reload. Fix:
    // App.js is now the SINGLE owner of the roster. patchTaw is threaded
    // down through AppRouter to ManageWorkspacePage (which no longer calls
    // useTaws() itself), so ManageWorkspacePage's split/policy/rename/
    // member-removal wrappers patch THIS instance, and every consumer --
    // TawsTab AND the scope chips -- re-renders from the same array
    // reference. This also removes N redundant get() reads per Workspace
    // visit (two instances of N get()s -> one).
    const { taws, patchTaw } = useTaws(
        isParentWorkspaceOwner ? selectedWorkspaceId : null,
        isParentWorkspaceOwner ? selectedWorkspace?.teamAgentWorkspaceIds : null
    );

    // Fold the resolved TAW docs into useWorkspaceList's OWN allWorkspaces
    // state (D-03 superseded; see src/hooks/useWorkspaceList.js's comment on
    // setExtraWorkspaces). Declared AFTER `taws` exists — never as a
    // positional argument to useWorkspaceList at its call site above, which
    // would be a TDZ crash (taws isn't declared until here).
    React.useEffect(() => {
        setExtraWorkspaces(taws);
    }, [taws, setExtraWorkspaces]);

    const [teamScope, setTeamScope] = React.useState('own');
    // TAW-03 (41-09, D-17): the archive year selector, lifted here so it can
    // window the TAW query below. ARCHIVE_YEAR_ALL default matches
    // TransactionsArchivePage's own uncontrolled default, so a caller that
    // doesn't yet pass yearFilter/onYearFilterChange (none does before this
    // plan wires AppRouter.js) sees byte-identical behavior. isArchiveView
    // reuses the EXISTING transactionView derivation above -- never a second
    // useLocation read.
    const [archiveYearFilter, setArchiveYearFilter] = React.useState(ARCHIVE_YEAR_ALL);
    const isArchiveView = transactionView === 'archive';
    // Memoized: an inline { kind: 'archive', year } object literal would
    // otherwise get a fresh identity every render and thrash
    // useTawTransactions' scopeKey effect-dedup key, reopening reads
    // continuously (T-41-09-05). computeTawScope/computeEffectiveTeamScope
    // are a matched pair (see tawArchiveScope.js's header) -- always used
    // together.
    const tawScope = React.useMemo(
        () => computeTawScope({ isArchiveView, archiveYearFilter }),
        [isArchiveView, archiveYearFilter]
    );
    const effectiveTeamScope = computeEffectiveTeamScope({ isArchiveView, archiveYearFilter, teamScope });
    // TRUE state (D-06): `CappingStatusCard` does not exist anywhere in
    // `src/`. The capping projection at `IncomeV2Page.js:121-122` receives a
    // hard-coded `tawPipelines: []` and never reads `tawTransactions`, so
    // there is no live consumer that needs every TAW's transactions
    // subscribed regardless of the picker. The only live consumer is
    // `AppRouter.js`'s `displayTransactions`, which is already fully
    // scope-filtered by `teamScope`. `teamScope` is therefore threaded into
    // the subscription itself (not just the display): `'own'` acquires zero
    // slices for any TAW count, a specific TAW acquires exactly one, and
    // `'all'` acquires one per TAW.
    //
    // D-17 (41-09): the third argument is now `effectiveTeamScope`, not the
    // raw `teamScope` state -- on the archive route with 'All years'
    // selected, effectiveTeamScope pins to 'own' so this hook acquires ZERO
    // TAW slices rather than being handed a degraded/malformed archive
    // scope. The fourth argument (`tawScope`) is the whole point of this
    // plan: 41-05 built it and left it unpassed.
    const tawTransactions = useTawTransactions(
        isParentWorkspaceOwner ? selectedWorkspaceId : null,
        taws,
        effectiveTeamScope,
        tawScope
    );
    // Reset scope when switching workspaces
    React.useEffect(() => { setTeamScope('own'); }, [selectedWorkspaceId]);
    // Reset the archive year filter on workspace switch too, so a year
    // picked in one workspace never leaks into another -- a SEPARATE new
    // effect, deliberately not merged into, reordered around, or otherwise
    // combined with the scope-reset effect immediately above (D-24 pins that
    // effect verbatim; Phase 40's D-01 zero-listener boot census leans on
    // it). This effect opens no listener and performs no read.
    React.useEffect(() => { setArchiveYearFilter(ARCHIVE_YEAR_ALL); }, [selectedWorkspaceId]);

    // WR-04 (39-22, Task 3): hard-drop every foreign-workspace store slice on
    // switch. Per-workspace data freshness is ALREADY structural -- every
    // listener effect (useWorkspaceData's statuses, AppRouter's/App.js's own
    // gated leases) re-subscribes on its own [selectedWorkspaceId] dependency,
    // and every store slice key is workspaceId-outermost (D-28,
    // src/store/sliceKeys.js's serializeScope) -- so a workspace-B read can
    // never surface a workspace-A slice by construction. dropForeignWorkspace
    // adds a HARD EVICTION of stale foreign-workspace slices on top of that
    // structural guarantee, so a slice a prior workspace acquired (and is
    // still within its WORKSPACE_STORE_GRACE_TTL_MS cache window) doesn't
    // linger in the module-level store Map any longer than necessary once the
    // user has switched away from it. This is NOT a replacement for a prior
    // data-reset mechanism -- useWorkspaceData.js's prevWorkspaceIdRef (see
    // its own docblock) only ever gated the reset of the transactionsLoaded/
    // contactsLoaded skeleton latch, which 39-22 Task 2 already deleted; there
    // was no data-eviction behavior to replace.
    React.useEffect(() => {
        if (!selectedWorkspaceId) return;
        dropForeignWorkspace(selectedWorkspaceId);
    }, [selectedWorkspaceId]);

    // True when a TW owner OR MEMBER is currently viewing one of their TAWs.
    // Widened alongside isParentWorkspaceOwner (D-02): a member gets the same
    // Sidebar "back to team" affordance as the owner, not just the
    // WorkspaceSelector dropdown's D-05 exit path.
    const isViewingChildAsParent = React.useMemo(() => {
        if (!selectedWorkspace || selectedWorkspace.teamRole !== 'team_agent') return false;
        const twId = selectedWorkspace.parentWorkspaceId;
        if (!twId) return false;
        const uid = user?.uid;
        if (!uid) return false;
        const tw = allWorkspaces.find(w => w.id === twId);
        if (!tw) return false;
        return Boolean(
            tw.ownerId === uid
            || (Array.isArray(tw.members) && tw.members.includes(uid))
            || Boolean(tw.memberDirectory?.[uid])
        );
    }, [selectedWorkspace, allWorkspaces, user]);

    const tw = React.useMemo(() => {
        if (!isViewingChildAsParent || !selectedWorkspace?.parentWorkspaceId) return null;
        return allWorkspaces.find(w => w.id === selectedWorkspace.parentWorkspaceId) || null;
    }, [isViewingChildAsParent, selectedWorkspace, allWorkspaces]);

    const handleBackToParent = React.useCallback(() => {
        if (tw) setSelectedWorkspaceId(tw.id);
    }, [tw, setSelectedWorkspaceId]);

    const isTcModeEnabled = Boolean(userSettings?.isTcModeEnabled);
    const wizardInitialNames = React.useMemo(() => {
        const existingFirst = (userSettings.firstName || '').trim();
        const existingLast = (userSettings.lastName || '').trim();
        if (existingFirst || existingLast) {
            return { firstName: existingFirst, lastName: existingLast };
        }
        const displayName = user && user.displayName ? user.displayName.trim() : '';
        if (displayName) {
            const parts = displayName.split(' ').filter(Boolean);
            if (parts.length === 1) {
                return { firstName: parts[0], lastName: '' };
            }
            const [first, ...rest] = parts;
            return { firstName: first || '', lastName: rest.join(' ') || '' };
        }
        return { firstName: '', lastName: '' };
    }, [userSettings.firstName, userSettings.lastName, user]);

    const {
        workspaceDataLoaded,
        statuses, statusOrder, customStatuses,
        persistStatusOrder, persistStatusMetadata, applyLocalStatusOrder
    } = useWorkspaceData(user, selectedWorkspaceId, selectedWorkspace, updateSelectedWorkspace);

    // DATA-01/DATA-04 (39-22, Task 2): `transactions` and `workspaceContacts`
    // no longer come from useWorkspaceData's boot listeners (both deleted --
    // see useWorkspaceData.js's file-header comment). App.js's own
    // write-path handlers that still need them (useExpenseHandlers,
    // useIncomeHandlers,
    // useTransactionHandlers, useContactHandlers, the contacts-migration
    // one-shot effect, the selectedTransaction refresh effect) now read
    // these gated on-demand leases instead -- `transactions` reuses
    // AppRouter's own all-scope shape (39-18's useTransactions with
    // scope: { kind: 'all' }) since several of these handlers look up a
    // transaction by id regardless of which year it closed in (e.g.
    // useContactHandlers' cascade update across every transaction
    // referencing an edited contact); `workspaceContacts` reuses the exact
    // same useLeasedCollection('contacts', ...) machine AppRouter.js's own
    // gated lease uses (39-22, Task 1). Both variable names are kept
    // identical to the retired useWorkspaceData fields so every downstream
    // consumer below needs zero further changes.
    // Final review F4: a brokerage switch or plan edit (made from Settings,
    // where these leases are normally closed) re-prices the open deals under
    // the new brokerage. Until that resync has run, the deals lease (and, for a
    // team leader, the team-split records) stay open even on Settings.
    // Bound to the workspace it was scheduled for: a change of workspace drops
    // it, so another workspace's deals are never resynced with this brokerage.
    const pendingResync = usePendingBrokerageResync(selectedWorkspaceId);
    const pendingBrokerageResync = pendingResync.brokerage;
    const schedulePendingResync = pendingResync.schedule;
    const transactionsGate = (TRANSACTION_DATA_VIEWS.has(currentView) || pendingBrokerageResync) ? selectedWorkspaceId : undefined;
    const transactions = useTransactions({
        scope: { kind: 'all' },
        workspaceId: transactionsGate,
    });
    const transactionsLoaded = useTransactionsLoaded({ scope: { kind: 'all' }, workspaceId: transactionsGate });
    const workspaceContacts = useLeasedCollection('contacts', TRANSACTION_DATA_VIEWS.has(currentView) ? selectedWorkspaceId : undefined);

    // D-20 (39-17, Task 1/3): the 13 D-20 collections (+ workspaceIncome,
    // formerly a useWorkspaceData boot listener) App.js's OWN write-path
    // handlers still need -- leased per-route via the store, gated by
    // currentView (VENDOR_DATA_VIEWS/EMAIL_ASSET_VIEWS/EXPENSE_TYPE_VIEWS/
    // INCOME_TYPE_VIEWS/TRANSACTION_WRITE_VIEWS above) rather than always-on
    // like the old useWorkspaceData listeners. workspaceExpenses has no
    // App.js-level write-path consumer (confirmed via grep) and is not
    // re-leased here -- ExpensesV2Page/IncomeV2Page/DataTab lease it
    // directly where it's actually needed.
    const lenders = useLenders(VENDOR_DATA_VIEWS.has(currentView) ? selectedWorkspaceId : undefined, selectedWorkspace);
    const titleCompanies = useTitleCompanies(VENDOR_DATA_VIEWS.has(currentView) ? selectedWorkspaceId : undefined, selectedWorkspace);
    const realtors = useRealtors(VENDOR_DATA_VIEWS.has(currentView) ? selectedWorkspaceId : undefined, selectedWorkspace);
    const emailTemplates = useEmailTemplatesLease(EMAIL_ASSET_VIEWS.has(currentView) ? selectedWorkspaceId : undefined, selectedWorkspace);
    const expenseTypes = useLeasedCollection('expenseTypes', EXPENSE_TYPE_VIEWS.has(currentView) ? selectedWorkspaceId : undefined);
    const incomeTypes = useLeasedCollection('incomeTypes', INCOME_TYPE_VIEWS.has(currentView) ? selectedWorkspaceId : undefined);
    const workspaceIncomeGate = (TRANSACTION_WRITE_VIEWS.has(currentView) || pendingBrokerageResync) ? selectedWorkspaceId : undefined;
    const workspaceIncome = useLeasedCollection('workspaceIncome', workspaceIncomeGate);
    // A plan team leader's period cache sums every team-split record, so it is
    // refreshed only once this lease has delivered them (useTransactionHandlers).
    const workspaceIncomeLoaded = useLeasedCollectionLoaded('workspaceIncome', workspaceIncomeGate);

    const { connected: googleConnected, initiate: initiateGoogleOAuth } = useGoogleOAuthStatus(
        user,
        (msg) => showToast(msg, 'error')
    );

    const authContextValue = React.useMemo(() => ({
        user,
        isTcModeEnabled,
        hasGmailAccess: googleConnected,
        ensureGmailAccess: initiateGoogleOAuth,
        // Owner of the active workspace; gates Remove from deal in the email reader (D-24).
        canManageWorkspaceAccess,
    }), [user, isTcModeEnabled, googleConnected, initiateGoogleOAuth, canManageWorkspaceAccess]);

    // Seeds ONLY the users/{uid} profile document for a brand-new signup.
    //
    // This used to also hand-roll a workspaces document in the same batch, with
    // no userAccount doc alongside it. The account-status effect below then
    // called ensureUserAccount, which keys entirely off userAccount/{uid},
    // found nothing, and provisioned a SECOND workspace plus a billable
    // usageAssignments doc -- so every brand-new self-serve signup landed in
    // the shell with two workspaces in the switcher, the real one invisible to
    // the account-lifecycle machinery (no teamRole, no status, no
    // consumedMemberSlots) and the phantom one holding the tier assignment.
    //
    // ensureUserAccount is now the SOLE owner of workspace, userAccount, and
    // usageAssignments provisioning; it does not write users/{uid}, so that
    // write stays here. dataEntryDefaults is deliberately not seeded -- the
    // server callable does not write it either, and App.js falls back to
    // DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS when a workspace has none (the
    // same shape every workspace from handleCreateWorkspace already has).
    const signInWithGoogle = () => {
        const provider = createGoogleProvider();
        signInWithPopup(auth, provider)
            .then(async (result) => {
                // Delegates to src/services/userProfile.js so this write has ONE
                // home. /signup's CTA is a second sign-in entrance that must
                // seed the same document; a copy there would be a copy that can
                // drift.
                await ensureUserProfileDocument(result.user);
            })
            .catch((error) => console.error("Authentication error:", error));
    };

    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            authResolvedRef.current = true;
            setAuthResolved(true);
            setUser(currentUser);
            if (!currentUser) {
                // Collection data and user settings are reset by their own hooks
                // when user becomes null. Reset only App.js-owned state here.
                setIsStatusManagerOpen(false);
                setEmailTemplateFolders([]);
                setWorkspaceDataEntrySettings(DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS);
                setShowOnboardingWizard(false);
                setHasCompletedOnboardingSession(false);
                setTcOverlayVisible(false);
                setAccountStatus(null);
                setAccountPurgeAfter(null);
                setAccountStatusReason(null);
                setAccountCascadeDeleted(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // Task 7 (D-10): ensureUserAccount is the first callable a signed-in user
    // needs so AccountDeletedScreen can gate below, and -- since
    // signInWithGoogle above no longer creates one -- it is also what
    // provisions a brand-new user's workspace, userAccount, and
    // usageAssignments docs. It must never fire while the user is still on one
    // of the public, pre-shell routes (/welcome, /accept-invite, ...):
    // landing there while already signed in (mid Google sign-in popup) and
    // calling it before acceptance runs would provision a default-tier
    // workspace ahead of the invitation's chosen tier, exactly the race
    // WelcomePage.js's and InvitationShell.js's header comments describe.
    // Those pages deliberately never import this callable themselves; gating
    // on the CURRENT route here (not just on `user` truthiness) is what keeps
    // that guarantee intact now that something in App.js finally calls it on
    // the ordinary sign-in path. isPublicRoutePath matches with the router's
    // own matcher rather than an exact string compare, so /welcome/ and
    // /Welcome -- both of which really do render WelcomePage -- are gated too.
    // accountStatusFetchedForUidRef dedupes to exactly one call per signed-in
    // uid so navigating between routes after the shell has booted never
    // re-fires it.
    const accountStatusFetchedForUidRef = React.useRef(null);
    React.useEffect(() => {
        if (!user) {
            accountStatusFetchedForUidRef.current = null;
            return;
        }
        if (isPublicRoutePath(location.pathname)) return;
        if (accountStatusFetchedForUidRef.current === user.uid) return;
        accountStatusFetchedForUidRef.current = user.uid;
        invitationService.ensureUserAccount()
            .then((result) => {
                setAccountStatus(result?.status || 'active');
                setAccountPurgeAfter(result?.purgeAfter || null);
                setAccountStatusReason(result?.statusReason || null);
                setAccountCascadeDeleted(result?.cascadeDeleted === true);
            })
            .catch((error) => {
                console.error('Failed to resolve account status:', error);
                // Clear the dedupe key so the NEXT run of this effect retries.
                // The effect is keyed on [user, location.pathname], so that is
                // the next navigation (or the next sign-in) -- clearing a ref
                // does not itself schedule a re-render, and a plain re-render
                // would not re-run the effect anyway. accountStatus stays null
                // ("not yet known") until then, which renders the app rather
                // than the deleted screen.
                accountStatusFetchedForUidRef.current = null;
            });
    }, [user, location.pathname]);

    // MEAS-02 (Phase 35, D-05/D-06): boot -> first-meaningful-paint span.
    // Fires exactly once, once auth has resolved and, for a signed-in user,
    // once userSettings has finished loading — a reasonable proxy for "the
    // shell has enough data to render its first meaningful screen". A
    // signed-out user has nothing further to load, so the logged-out path
    // fires as soon as auth resolves. WR-01: the effect is keyed on the
    // authResolved STATE flag (not just user) so it re-runs even for a
    // signed-out session, where user stays null and would otherwise never
    // trigger a re-render. Feature-detects performance so this never throws
    // in a non-standard environment; the 'boot-start' mark is set as early as
    // possible in index.js.
    React.useEffect(() => {
        if (bootFmpMarkedRef.current) return;
        if (!authResolvedRef.current) return;
        if (user && !userSettingsLoaded) return;
        bootFmpMarkedRef.current = true;
        if (typeof performance === 'undefined' || typeof performance.mark !== 'function') return;
        try {
            performance.mark('boot-fmp');
            const measure = performance.measure('boot-duration', 'boot-start', 'boot-fmp');
            const span = Sentry.startInactiveSpan({ name: 'boot-duration' });
            span.setAttribute('duration_ms', measure.duration);
            span.end();
        } catch (err) {
            // 'boot-start' may be missing in a non-standard environment (e.g. no
            // index.js entry ran first) — never let a measurement failure block boot.
        }
    }, [authResolved, user, userSettingsLoaded]);

    // BOOT-03 (D-05/D-06): idle-prefetch the top three routes (Dashboard,
    // transactions list, clients) once, after first paint. Feature-detects
    // requestIdleCallback with a setTimeout fallback (Safari has no native
    // requestIdleCallback); prefetchTopRoutes itself backs off on Save-Data
    // or a slow connection and never throws, so this effect never blocks or
    // interferes with the boot-fmp measurement above.
    React.useEffect(() => {
        if (idlePrefetchRanRef.current) return;
        idlePrefetchRanRef.current = true;
        const scheduleIdle = (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function')
            ? window.requestIdleCallback
            : (fn) => setTimeout(fn, 1);
        scheduleIdle(() => prefetchTopRoutes());
    }, []);

    React.useEffect(() => {
        if (transactionView !== 'list' && isStatusManagerOpen) {
            setIsStatusManagerOpen(false);
        }
    }, [transactionView, isStatusManagerOpen]);

    // DATA-01 (39-22, Task 2): `transactions` is now App.js's own gated
    // TRANSACTION_DATA_VIEWS lease (declared above), not a useWorkspaceData
    // boot listener -- this effect only has fresh data to refresh from while
    // 'form' (or another gated view) is active, which is exactly when a
    // selectedTransaction exists to refresh.
    React.useEffect(() => {
        if (!selectedTransaction?.id) return;
        const refreshedTransaction = transactions.find(transaction => transaction.id === selectedTransaction.id);
        if (!refreshedTransaction) return;
        setSelectedTransaction(prev => {
            if (!prev?.id || prev.id !== refreshedTransaction.id) return prev;
            return refreshedTransaction;
        });
    }, [selectedTransaction?.id, transactions]);


    // NOTE: the statuses collection subscription is managed by
    // useWorkspaceData; transactions/contacts are App.js's own gated leases
    // declared above (39-22, Task 2). The reset on workspace change for
    // statuses is handled inside useWorkspaceData itself.
    React.useEffect(() => {
        if (!selectedWorkspaceId) {
            setIsStatusManagerOpen(false);
        }
    }, [selectedWorkspaceId]);

    React.useEffect(() => {
        // SA-01: If the wizard was force-opened by the Help drawer ("Replay setup"), consume
        // the flag only once the wizard is actually visible, then let it stay open. Consuming
        // it before the wizard is open would cause the flag to be silently swallowed on an
        // unrelated effect run and suppress the next legitimate auto-open.
        if (forceOpenOnboardingRef.current) {
            if (showOnboardingWizard) forceOpenOnboardingRef.current = false;
            return;
        }
        if (!user || !userSettingsLoaded) {
            setShowOnboardingWizard(false);
            return;
        }
        if (userSettings.onboardingCompleted) {
            setShowOnboardingWizard(false);
            return;
        }
        // Bug: 2026-09-24-onboarding-wizard-before-plan-choice.md. The wizard's steps
        // are workspace-scoped (the eXp Anniversary step writes via
        // updateWorkspaceSettings(selectedWorkspaceId, ...); Connect Google calls
        // oauth.initiate(selectedWorkspaceId)) -- auto-opening before a workspace
        // exists (e.g. a brand-new account still on /signup choosing a plan) means
        // those writes have nowhere to go, and the wizard would still mark
        // onboardingCompleted, so it would never reappear once a workspace exists.
        // Wait for a real selected workspace before auto-opening; this effect re-runs
        // once one is selected (e.g. right after the user lands in their first
        // workspace post-plan-choice).
        if (!selectedWorkspaceId) {
            setShowOnboardingWizard(false);
            return;
        }
        if (!hasCompletedOnboardingSession) {
            setShowOnboardingWizard(true);
        }
    }, [user, userSettingsLoaded, userSettings.onboardingCompleted, hasCompletedOnboardingSession, showOnboardingWizard, selectedWorkspaceId]);

    React.useEffect(() => {
        if (!userSettingsLoaded || showOnboardingWizard) {
            setTcOverlayVisible(false);
            return;
        }
        if (!isTcModeEnabled) {
            setTcOverlayVisible(false);
            return;
        }
        if (!userSettings.tcOverlayDismissed) {
            setTcOverlayVisible(true);
        }
    }, [userSettingsLoaded, showOnboardingWizard, isTcModeEnabled, userSettings.tcOverlayDismissed]);

    React.useEffect(() => {
        if (isTcModeEnabled && (location.pathname === '/expenses' || location.pathname === '/income')) {
            setSelectedTransaction(null);
            setTransactionFormTab('details');
            rrNavigate('/');
        }
    }, [isTcModeEnabled, location.pathname, rrNavigate]);
    React.useEffect(() => {
        if (!isMobileViewport) {
            setIsMobileNavOpen(false);
        }
    }, [isMobileViewport]);
    React.useEffect(() => {
        setIsMobileNavOpen(false);
    }, [location.pathname]);

    // Rebuilt from the whole stored location, not just its pathname —
    // returnToFromState owns that contract. See ProtectedRoute.js.
    const loginReturnTo = returnToFromState(location.state);
    React.useEffect(() => {
        if (user && location.pathname === '/login') {
            rrNavigate(loginReturnTo, { replace: true });
        }
    }, [user, location.pathname, loginReturnTo, rrNavigate]);
    React.useEffect(() => {
        if (!selectedWorkspace) {
            setEmailTemplateFolders([]);
            return;
        }
        const folders = Array.isArray(selectedWorkspace.emailTemplateFolders)
            ? selectedWorkspace.emailTemplateFolders
                .map(folder => ({
                    id: typeof folder?.id === 'string' ? folder.id : '',
                    name: (folder?.name || '').trim(),
                    createdAt: folder?.createdAt || null,
                    updatedAt: folder?.updatedAt || null,
                    targetClients: normalizeTemplateTargetClients(folder?.targetClients)
                }))
                .filter(folder => folder.id && folder.name)
                .sort((a, b) => a.name.localeCompare(b.name))
            : [];
        setEmailTemplateFolders(folders);
    }, [selectedWorkspace]);

    React.useEffect(() => {
        if (selectedWorkspace?.dataEntryDefaults) {
            setWorkspaceDataEntrySettings(sanitizeWorkspaceDataEntrySettings(selectedWorkspace.dataEntryDefaults));
        } else {
            setWorkspaceDataEntrySettings(DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS);
        }

        // Load eXp module settings and auto-reset period counters on anniversary date.
        const loadedExp = sanitizeExpModuleSettings(selectedWorkspace?.expModuleSettings);
        // For child workspaces the parent-configured splitPercentToAgent is the agent's
        // effective team split — apply it so the commission pipeline uses the right value.
        if (selectedWorkspace?.teamRole === 'team_agent' && selectedWorkspace.splitPercentToAgent != null) {
            loadedExp.teamSplitPercentage = Number(selectedWorkspace.splitPercentToAgent);
        }
        if (loadedExp.anniversaryDate) {
            try {
                const [month, day] = loadedExp.anniversaryDate.split('-').map(Number);
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    const now = new Date();
                    const thisYearAnn = new Date(now.getFullYear(), month - 1, day);
                    const lastAnn = (thisYearAnn <= now ? thisYearAnn : new Date(now.getFullYear() - 1, month - 1, day))
                        .toISOString().split('T')[0];
                    if (!loadedExp.cappingPeriodStart || lastAnn > loadedExp.cappingPeriodStart) {
                        const reset = {
                            ...loadedExp,
                            companyDollarPaidCurrentPeriod: 0,
                            cappedStatusFeePaidCurrentPeriod: 0,
                            riskManagementFeePaidCurrentPeriod: 0,
                            // Clear own-contribution caches so the next sync triggers a fresh
                            // first-run reseed for the new period (re-summing children).
                            companyDollarPaidOwnCurrentPeriod: null,
                            cappedStatusFeePaidOwnCurrentPeriod: null,
                            riskManagementFeePaidOwnCurrentPeriod: null,
                            cappingPeriodStart: lastAnn,
                        };
                        setExpModuleSettings(reset);
                        if (selectedWorkspaceId) {
                            workspaceService.updateWorkspaceSettings(selectedWorkspaceId, { expModuleSettings: reset });
                        }
                        return;
                    }
                }
            } catch (_) { /* malformed anniversaryDate — fall through */ }
        }
        setExpModuleSettings(loadedExp);
    }, [selectedWorkspace, selectedWorkspaceId]);

    // SENTRY-02: tag every Sentry event with user identity, workspace, role, and
    // build version so issues can be triaged by agent, workspace, and release.
    // Depends on: user (line 86), selectedWorkspaceId (line 87),
    //             selectedWorkspace (line 179), and userSettings (line 158).
    React.useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.uid, email: user.email });
            Sentry.setTag('workspace_id',   selectedWorkspaceId ?? 'none');
            Sentry.setTag('workspace_role', selectedWorkspace?.teamRole ?? 'solo');
            Sentry.setTag('user_role',      userSettings.role ?? 'realtor');
            Sentry.setTag('build_version',  buildInfo.version);
        } else {
            Sentry.setUser(null);
            Sentry.setTag('workspace_id',   null);
            Sentry.setTag('workspace_role', null);
            Sentry.setTag('user_role',      null);
        }
    }, [user, selectedWorkspaceId, selectedWorkspace, userSettings.role]);

    // 40-10: uidsNeeded is derived synchronously from selectedWorkspace's own
    // memberDirectory (the same uid set the member-fetch effect below used to
    // compute inline before awaiting the member-resolution callable
    // directly) -- entries other than the current user that lack an email or
    // a display name. useMemberProfiles is a hook, so it must be called
    // unconditionally at the component's top level, not from inside the
    // async effect body.
    const uidsNeededForMemberProfiles = React.useMemo(() => {
        if (!selectedWorkspace) return [];
        const { memberIds, memberDirectory } = computeWorkspaceMemberIdsAndDirectory(selectedWorkspace);
        const currentUid = typeof user?.uid === 'string' ? user.uid : '';
        return memberIds.filter(memberId => {
            if (memberId === currentUid) return false;
            const directoryEntry = memberDirectory[memberId] || {};
            const directoryEmail = typeof directoryEntry.email === 'string' ? directoryEntry.email : '';
            const directoryDisplayName = typeof directoryEntry.displayName === 'string' ? directoryEntry.displayName : '';
            return !directoryEmail.trim() || !directoryDisplayName.trim();
        });
    }, [selectedWorkspace, user?.uid]);

    // Shared, session-lifetime, workspace-scoped cache (40-04) -- a second
    // modal open or a second consumer requesting an already-resolved uid
    // costs zero member-resolution callable invocations (COLD-01).
    const memberProfiles = useMemberProfiles(selectedWorkspace?.id, uidsNeededForMemberProfiles);

    // This effect stays scoped to the self-lookup (the current user's own
    // users/{uid} doc, which regular members CAN read) and no longer awaits
    // the member-resolution callable directly -- that best-effort-continue
    // resolution (including its console.error) now lives inside the shared
    // hook above. workspaceMembers here holds the BASE (pre-enrichment,
    // pre-sort) entries; resolvedWorkspaceMembers below merges in whatever
    // memberProfiles currently has via a plain useMemo (not an effect), so a
    // profile resolving after this effect has already run still reaches the
    // UI without this effect's own dependency array ever needing to include
    // memberProfiles itself.
    React.useEffect(() => {
        if (!selectedWorkspace) {
            setWorkspaceMembers([]);
            return;
        }

        const { memberIds, memberDirectory } = computeWorkspaceMemberIdsAndDirectory(selectedWorkspace);
        if (memberIds.length === 0) {
            setWorkspaceMembers([]);
            return;
        }

        let isCancelled = false;
        const fetchMembers = async () => {
            try {
                const currentUid = typeof user?.uid === 'string' ? user.uid : '';
                const results = await Promise.all(memberIds.map(async (memberId) => {
                    const directoryEntry = memberDirectory[memberId] || {};
                    const directoryEmail = typeof directoryEntry.email === 'string' ? directoryEntry.email : '';
                    const directoryDisplayName = typeof directoryEntry.displayName === 'string' ? directoryEntry.displayName : '';
                    const baseEntry = {
                        uid: memberId,
                        email: directoryEmail.trim(),
                        displayName: directoryDisplayName.trim(),
                        photoURL: '',
                        isOwner: selectedWorkspace.ownerId === memberId,
                        addedAt: directoryEntry.addedAt || null,
                        missing: false
                    };
                    if (memberId !== currentUid) {
                        return baseEntry;
                    }
                    try {
                        const memberDoc = await workspaceService.getUserRecord(memberId);
                        if (memberDoc.exists()) {
                            const data = memberDoc.data() || {};
                            return {
                                ...baseEntry,
                                email: (data.email || baseEntry.email || '').trim(),
                                displayName: (data.displayName || baseEntry.displayName || '').trim(),
                                photoURL: data.photoURL || ''
                            };
                        }
                        return {
                            ...baseEntry,
                            missing: true
                        };
                    } catch (memberError) {
                        console.error('Failed to resolve workspace member document:', memberId, memberError);
                        return {
                            ...baseEntry,
                            missing: true
                        };
                    }
                }));

                if (isCancelled) return;
                setWorkspaceMembers(results.filter(Boolean));
            } catch (error) {
                if (!isCancelled) {
                    console.error("Error fetching members:", error);
                    setWorkspaceMembers([]);
                }
            }
        };

        fetchMembers();
        return () => {
            isCancelled = true;
        };
    }, [selectedWorkspace, user?.uid]);

    // Enrich the base entries with whatever the shared memberProfiles cache
    // currently has, then sort -- a plain derivation (useMemo, not an
    // effect), so it recomputes on every render where either input changed,
    // including the render triggered by memberProfiles resolving after the
    // fetch effect above already ran. This is what keeps a late-resolving
    // profile from being permanently stuck on its uid fallback.
    const resolvedWorkspaceMembers = React.useMemo(() => {
        const enriched = workspaceMembers.map(entry => {
            const profile = memberProfiles[entry.uid];
            if (!profile) return entry;
            if (entry.displayName && entry.email) return entry;
            const next = { ...entry };
            if (!next.displayName && profile.displayName) {
                next.displayName = profile.displayName.trim();
            }
            if (!next.email && profile.email) {
                next.email = profile.email.trim();
            }
            return next;
        });
        const labelFor = (entry) => (entry.displayName || entry.email || entry.uid || '').toLowerCase();
        return enriched.sort((a, b) => {
            if (a.isOwner === b.isOwner) {
                return labelFor(a).localeCompare(labelFor(b));
            }
            return a.isOwner ? -1 : 1;
        });
    }, [workspaceMembers, memberProfiles]);

    // D-20 (39-17, Task 1): realtorTeams/resolveTeamId/realtorTeamConfig
    // moved to TransactionFormV2.js -- the only consumer (grep-confirmed;
    // AppRouter never even threaded realtorTeamConfig down, a pre-existing
    // dead prop-pass this cutover incidentally fixed) -- computed there
    // from the form's own locally-leased `realtors`.

    const combinedTemplateFolders = useCombinedTemplateFolders({ emailTemplateFolders });

    // composerRecipients is built locally by TransactionFormV2.js (D-20, 39-17)
    // from its own leased vendor collections; App.js no longer builds a
    // recipient index because it has no send path (Phase 58-05).

    const {
        handleCreateChecklistTemplate,
        handleUpdateChecklistTemplate,
        handleDeleteChecklistTemplate,
    } = useEmailTemplateHandlers({
        selectedWorkspaceId,
        user,
        emailTemplates,
        emailTemplateFolders,
        combinedTemplateFolders,
        run,
        showToast,
    });

    // Computed above the handlers that price deals: this body sits above <BrokerageProvider>.
    const brokerage = React.useMemo(() => normalizeBrokerage(selectedWorkspace || {}), [selectedWorkspace]);

    const {
        handleCreateExpenseType,
        handleUpdateExpenseType,
        handleMoveExpenseType,
        handleDeleteExpenseType,
        handleCreateWorkspaceExpense,
        handleUpdateWorkspaceExpense,
        handleUpdateTransactionExpense,
        handleDeleteTransactionExpense,
        handleDeleteWorkspaceExpense,
    } = useExpenseHandlers({
        selectedWorkspaceId,
        user,
        expenseTypes,
        transactions,
        showToast,
        expModuleSettings,
        brokerage,
    });

    const {
        handleCreateIncomeType,
        handleUpdateIncomeType,
        handleMoveIncomeType,
        handleDeleteIncomeType,
        handleCreateWorkspaceIncome,
        handleUpdateWorkspaceIncome,
        handleDeleteWorkspaceIncome,
    } = useIncomeHandlers({
        selectedWorkspaceId,
        user,
        incomeTypes,
        transactions,
        showToast,
    });

    const { handleSaveWorkspaceDefaults, isSavingWorkspaceDefaults } = useWorkspaceDefaultsHandler({
        user,
        selectedWorkspaceId,
        showToast,
        setWorkspaceDataEntrySettings,
    });

    const handleSaveExpModuleSettings = React.useCallback(async (updates = {}) => {
        if (!selectedWorkspaceId) return;
        const sanitized = sanitizeExpModuleSettings(updates);
        try {
            await workspaceService.updateWorkspaceSettings(selectedWorkspaceId, { expModuleSettings: sanitized });
            setExpModuleSettings(sanitized);
            showToast('Commission settings saved.');
        } catch (error) {
            console.error('Failed to save eXp module settings:', error);
            showToast(writeErrorMessage(error, 'Failed to save commission settings.'), 'error');
        }
    }, [selectedWorkspaceId, showToast]);

    const handleUploadWorkspaceLogo = React.useCallback(async (file) => {
        if (!selectedWorkspaceId) return;
        const logoUrl = await workspaceService.uploadWorkspaceLogo(selectedWorkspaceId, file);
        showToast('Company logo updated.', 'success');
        return logoUrl;
    }, [selectedWorkspaceId, showToast]);

    const handleDeleteWorkspaceLogo = React.useCallback(async () => {
        if (!selectedWorkspaceId) return;
        const path = selectedWorkspace?.logoStoragePath || null;
        await workspaceService.deleteWorkspaceLogo(selectedWorkspaceId, path);
        showToast('Company logo removed.', 'success');
    }, [selectedWorkspaceId, selectedWorkspace, showToast]);

    const handleUploadWorkspaceDarkLogo = React.useCallback(async (file) => {
        if (!selectedWorkspaceId) return;
        const logoDarkUrl = await workspaceService.uploadWorkspaceDarkLogo(selectedWorkspaceId, file);
        showToast('Dark-mode logo updated.', 'success');
        return logoDarkUrl;
    }, [selectedWorkspaceId, showToast]);

    const handleDeleteWorkspaceDarkLogo = React.useCallback(async () => {
        if (!selectedWorkspaceId) return;
        const path = selectedWorkspace?.logoDarkStoragePath || null;
        await workspaceService.deleteWorkspaceDarkLogo(selectedWorkspaceId, path);
        showToast('Dark-mode logo removed.', 'success');
    }, [selectedWorkspaceId, selectedWorkspace, showToast]);

    const handleUploadUserHeadshot = React.useCallback(async (file) => {
        if (!user) return;
        const headshotUrl = await workspaceService.uploadUserHeadshot(user.uid, file);
        showToast('Headshot updated.', 'success');
        return headshotUrl;
    }, [user, showToast]);

    const handleDeleteUserHeadshot = React.useCallback(async () => {
        if (!user) return;
        const path = userSettings?.headshotStoragePath || null;
        await workspaceService.deleteUserHeadshot(user.uid, path);
        showToast('Headshot removed.', 'success');
    }, [user, userSettings, showToast]);

    const handleUploadUserBusinessCard = React.useCallback(async (file) => {
        if (!user) return;
        const businessCardUrl = await workspaceService.uploadUserBusinessCard(user.uid, file);
        showToast('Business card updated.', 'success');
        return businessCardUrl;
    }, [user, showToast]);

    const handleDeleteUserBusinessCard = React.useCallback(async () => {
        if (!user) return;
        const path = userSettings?.businessCardStoragePath || null;
        await workspaceService.deleteUserBusinessCard(user.uid, path);
        showToast('Business card removed.', 'success');
    }, [user, userSettings, showToast]);

    const {
        upsertWorkspaceContacts,
        addTransactionHeadless,
        handleAddTransaction,
        handleCreateTransaction,
        handleDuplicateTransaction,
        handleUpdateTransaction,
        handleDeleteTransaction,
        handleSetAnniversaryMuted,
        handleTransactionDrop,
        resyncOpenDeals,
    } = useTransactionHandlers({
        selectedWorkspaceId,
        selectedWorkspace,
        run,
        showToast,
        navigateToTransactions: () => rrNavigate('/transactions'),
        allTransactions: transactions,
        workspaceIncome,
        workspaceIncomeLoaded,
        expModuleSettings,
        onExpModuleSettingsChange: setExpModuleSettings,
        user,
        brokerage,
    });

    // The new brokerage is not in React state when the write resolves, so it is
    // built from what was written and handed to the open-deal resync (F4).
    const saveBrokeragePlan = React.useCallback(async (plan) => {
        if (!selectedWorkspaceId) return;
        try {
            const written = await workspaceService.saveBrokeragePlan(selectedWorkspaceId, plan);
            showToast('Brokerage saved.');
            if (written) {
                schedulePendingResync(selectedWorkspaceId, normalizeBrokerage({
                    brokerage: { ...brokerage, current: { ...brokerage.current, ...written } },
                }));
            }
        } catch (error) {
            console.error('Failed to save brokerage plan:', error);
            showToast(writeErrorMessage(error, 'Failed to save brokerage.'), 'error');
            throw error;
        }
    }, [selectedWorkspaceId, showToast, brokerage, schedulePendingResync]);

    const switchBrokerage = React.useCallback(async (choice) => {
        if (!selectedWorkspaceId) return;
        try {
            const result = await workspaceService.switchBrokerage(selectedWorkspaceId, choice);
            showToast('Brokerage changed.');
            if (result?.brokerage) schedulePendingResync(selectedWorkspaceId, normalizeBrokerage({ brokerage: result.brokerage }));
            return result;
        } catch (error) {
            console.error('Failed to change brokerage:', error);
            showToast(writeErrorMessage(error, 'Failed to change brokerage.'), 'error');
            throw error;
        }
    }, [selectedWorkspaceId, showToast, schedulePendingResync]);

    // Runs the pending resync once the deals (and a team leader's team-split
    // records) have loaded.
    const teamRole = selectedWorkspace?.teamRole || null;
    useRunPendingBrokerageResync({
        pending: pendingResync,
        ready: transactionsLoaded && (teamRole !== 'team' || workspaceIncomeLoaded),
        resyncOpenDeals,
    });

    const brokerageContextValue = React.useMemo(
        () => ({ brokerage, teamRole, saveBrokeragePlan, switchBrokerage }),
        [brokerage, teamRole, saveBrokeragePlan, switchBrokerage],
    );

    const { lenderHandlers, titleCompanyHandlers, realtorHandlers, createVendorOnce } = useVendorHandlers({
        selectedWorkspace,
        run,
        showToast,
    });

    // Phase 72: what the contract setup card writes on Approve. Same creation path as the
    // New deal form, at deterministic ids, with the outcome returned to the card (D-16).
    const dealWriters = {
        addTransactionHeadless,
        createVendorOnce,
        upsertWorkspaceContacts,
        updateTransaction: (id, patch, previousData) => transactionService.updateTransaction(
            selectedWorkspaceId, id, patch, previousData,
        ),
        getContact: (id) => workspaceService.getContact(selectedWorkspaceId, id),
        getTransaction: async (id) => (await import('./services/keyflowAiService'))
            .readDeal({ workspaceId: selectedWorkspaceId, dealId: id }),
        workspaceDefaults: workspaceDataEntrySettings,
        // The new or filled deal prices under the workspace's own brokerage plan.
        priceDeal: (deal) => deriveFinancialFieldsForModule(deal, buildPricingSettings({
            allTransactions: transactions,
            expModuleSettings,
            brokerage,
            currentTxId: deal.id,
            formData: deal,
        })),
    };

    const companyListHandlers = useCompanyListHandlers({
        selectedWorkspace,
        run,
        showToast,
    });

    const companyMigrationAttemptedRef = React.useRef(new Set());

    React.useEffect(() => {
        if (!selectedWorkspaceId) {
            companyMigrationAttemptedRef.current.clear();
            return;
        }
        const vendorTypeMap = [
            ['lenders', lenders],
            ['titleCompanies', titleCompanies],
            ['realtors', realtors],
        ];
        vendorTypeMap.forEach(([type, vendors]) => {
            if (!vendors.length) return;
            const key = `${selectedWorkspaceId}:${type}`;
            if (companyMigrationAttemptedRef.current.has(key)) return;
            companyMigrationAttemptedRef.current.add(key);
            companyListHandlers.migrateCompanies(type, vendors);
        });
        // One-shot migration guarded by companyMigrationAttemptedRef per
        // workspace:type; companyListHandlers is recreated each render, so we
        // intentionally key only on the workspace/vendor inputs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWorkspaceId, lenders, titleCompanies, realtors]);

    const { runContactsMigration } = useContactsMigration();

    const contactsMigrationAttemptedRef = React.useRef(new Set());

    React.useEffect(() => {
        if (!selectedWorkspaceId) {
            contactsMigrationAttemptedRef.current.clear();
            return;
        }
        if (!workspaceContacts?.length) return;
        const key = `${selectedWorkspaceId}:contacts`;
        if (contactsMigrationAttemptedRef.current.has(key)) return;
        contactsMigrationAttemptedRef.current.add(key);
        runContactsMigration(selectedWorkspaceId, workspaceContacts);
    }, [selectedWorkspaceId, workspaceContacts, runContactsMigration]);

    // D-20 (39-17, Task 1): lenderCompaniesConfig/titleCompanyCompaniesConfig/
    // realtorCompaniesConfig moved to useCompanyListConfig (src/store/hooks/),
    // called directly by BusinessPartnersPage.js/TransactionFormV2.js/
    // DataTab.js -- each now leases companyLists per-route instead of
    // reading the App.js-threaded object. companyListHandlers itself (pure
    // write functions, no data dependency) stays here, threaded down
    // unchanged.

    const {
        handleAddStatus,
        handleRenameStatus,
        handleDeleteStatus,
        handleBulkMoveTransactions,
        handleReorderStatus,
        handleMoveStatusBefore,
        handleSetStatusMinimized,
    } = useStatusHandlers({
        selectedWorkspaceId,
        selectedWorkspace,
        run,
        showToast,
        statuses,
        statusOrder,
        customStatuses,
        persistStatusOrder,
        persistStatusMetadata,
        applyLocalStatusOrder,
    });

    const {
        handleCreateWorkspace,
        handleRenameWorkspace,
        handleDeleteWorkspace,
        handleMoveTransaction,
        handleUpdateChildPolicy,
        handleUpdateChildSplit,
        handleRemoveTaw,
        handleOpenTaw,
        handleRenameChild,
        handleRemoveChildMember,
        handleAssignTransactionToChild,
        handleReassignTransaction,
    } = useWorkspaceHandlers({
        user,
        selectedWorkspaceId,
        selectedWorkspace,
        canManageWorkspaceAccess,
        run,
        showToast,
        setSelectedWorkspaceId,
        setCreateWorkspaceModalOpen,
        setDeleteWorkspaceModalOpen,
        setMoveTransactionModalOpen,
        setWorkspaceMembers,
        transactionToMove,
        setTransactionToMove,
        getWorkspaceName: (workspaceId) => (
            (taws || []).find((ws) => ws.id === workspaceId)?.name
            || (workspaces || []).find((ws) => ws.id === workspaceId)?.name
            || undefined
        ),
    });

    const {
        handleSearchGoogleContacts,
        handleSyncContactFromGoogle,
        applyReviewedSync,
        applyResolvedSync,
        handleDeleteWorkspaceContact,
        handleUpdateContact,
        handleCreateContact,
        handleCreateDealContact,
    } = useContactHandlers({
        selectedWorkspaceId,
        run,
        showToast,
        realtors,
        transactions,
        upsertWorkspaceContacts,
        workspaceContacts,
    });

    // D-20 (39-17, Task 1): _previewTemplateContext was dead code -- computed,
    // never read anywhere else in this file or threaded to AppRouter (grep-
    // confirmed) -- removed rather than rewired to the gated lenders/
    // titleCompanies/realtors below (Rule 1 cleanup).

    const realtorLinkedContactsConfig = React.useMemo(() => ({
        fieldName: 'transactionCoordinators',
        label: 'Transaction Coordinators',
        itemLabel: 'Coordinator',
        emptyText: 'Add up to 5 transaction coordinators to keep in the loop.',
        maxItems: 5,
        typeValue: 'realtor'
    }), []);

    const handleSaveUserSettings = async (updates = {}) => {
        if (!user) {
            showToast('Sign in to update your settings.', 'error');
            return;
        }
        const payload = { updatedAt: Date.now() };
        const assignString = (key, value) => {
            payload[key] = (value || '').trim();
        };

        if (Object.prototype.hasOwnProperty.call(updates, 'firstName')) assignString('firstName', updates.firstName);
        if (Object.prototype.hasOwnProperty.call(updates, 'lastName')) assignString('lastName', updates.lastName);
        if (Object.prototype.hasOwnProperty.call(updates, 'signature')) {
            payload.signature = updates.signature || '';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'lightTheme')) {
            payload.lightTheme = updates.lightTheme || DEFAULT_LIGHT_THEME;
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'darkTheme')) {
            payload.darkTheme = updates.darkTheme || DEFAULT_DARK_THEME;
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'defaultSignatureId')) {
            payload.defaultSignatureId = updates.defaultSignatureId || '';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'role')) {
            payload.role = updates.role === 'transactionCoordinator' ? 'transactionCoordinator' : 'realtor';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'isTcModeEnabled')) {
            payload.isTcModeEnabled = Boolean(updates.isTcModeEnabled);
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'tcOverlayDismissed')) {
            payload.tcOverlayDismissed = Boolean(updates.tcOverlayDismissed);
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'termsAcceptedAt')) {
            payload.termsAcceptedAt = updates.termsAcceptedAt || null;
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'onboardingCompleted')) {
            payload.onboardingCompleted = Boolean(updates.onboardingCompleted);
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'emailTemplatesDefaultSort')) {
            payload.emailTemplatesDefaultSort = updates.emailTemplatesDefaultSort === 'recent' ? 'recent' : 'name';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'emailTemplatesDefaultGroup')) {
            payload.emailTemplatesDefaultGroup = updates.emailTemplatesDefaultGroup === 'none' ? 'none' : 'audience';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'emailMarketingTemplatesDefaultSort')) {
            payload.emailMarketingTemplatesDefaultSort = updates.emailMarketingTemplatesDefaultSort === 'recent' ? 'recent' : 'name';
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'emailMarketingTemplatesDefaultGroup')) {
            payload.emailMarketingTemplatesDefaultGroup = updates.emailMarketingTemplatesDefaultGroup === 'none' ? 'none' : 'campaign';
        }
        const toggledOn = Object.prototype.hasOwnProperty.call(updates, 'isTcModeEnabled')
            && Boolean(updates.isTcModeEnabled)
            && !isTcModeEnabled;
        if (toggledOn && !Object.prototype.hasOwnProperty.call(updates, 'tcOverlayDismissed')) {
            payload.tcOverlayDismissed = false;
        }
        try {
            setIsSavingSettings(true);
            await workspaceService.saveUserSettings(user.uid, payload);
            showToast('Settings saved.');
        } catch (error) {
            console.error('Failed to save settings:', error);
            showToast(`Failed to save settings: ${error?.message || 'Unknown error'}`, 'error');
        } finally {
            setIsSavingSettings(false);
        }
    };

    const [isSavingWorkspaceAutomation, setIsSavingWorkspaceAutomation] = React.useState(false);
    const handleSaveWorkspaceAutomation = async (workspaceId, automationFields, automationKey = 'calendarAutomation') => {
        if (!workspaceId || !automationFields || typeof automationFields !== 'object') return;
        // Use dot notation per top-level key inside the named automation object
        // so server-managed fields (e.g. calendarAutomation.calendarId,
        // milestoneDigestAutomation.lastSentAt) aren't clobbered by a wholesale
        // replacement of the parent map.
        const updates = {};
        for (const [key, value] of Object.entries(automationFields)) {
            updates[`${automationKey}.${key}`] = value;
        }
        try {
            setIsSavingWorkspaceAutomation(true);
            await workspaceService.updateWorkspaceSettings(workspaceId, updates);
            showToast('Automation settings saved.');
        } catch (error) {
            console.error('Failed to save workspace automation:', error);
            showToast(`Failed to save automation: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        } finally {
            setIsSavingWorkspaceAutomation(false);
        }
    };

    const handleCompleteOnboarding = async ({ firstName, lastName, role, acceptedTermsVersion, acceptedAt }, { landOnDashboard = false } = {}) => {
        const trimmedFirst = (firstName || '').trim();
        const trimmedLast = (lastName || '').trim();
        // The wizard sends no role (C-CR-01): keep the stored role and TC mode,
        // so "Replay setup" never demotes a transaction coordinator. A brand-new
        // account (no stored role) becomes a realtor.
        const { role: normalizedRole, isTcMode, writesTcMode } = resolveOnboardingRole({
            payloadRole: role,
            userSettings,
        });
        const acceptedTimestamp = acceptedAt || Date.now();
        const updates = {
            firstName: trimmedFirst,
            lastName: trimmedLast,
            role: normalizedRole,
            ...(writesTcMode ? { isTcModeEnabled: isTcMode } : {}),
            onboardingCompleted: true,
            termsAcceptedAt: acceptedTimestamp,
            ...(acceptedTermsVersion ? { acceptedTermsVersion } : {}),
            ...(acceptedAt ? { acceptedAt } : {}),
        };
        if (isTcMode) {
            updates.tcOverlayDismissed = false;
        }

        setHasCompletedOnboardingSession(true);
        setShowOnboardingWizard(false);
        setUserSettings(prev => ({
            ...prev,
            firstName: trimmedFirst,
            lastName: trimmedLast,
            role: normalizedRole,
            ...(writesTcMode ? { isTcModeEnabled: isTcMode } : {}),
            onboardingCompleted: true,
            termsAcceptedAt: acceptedTimestamp,
            ...(acceptedTermsVersion ? { acceptedTermsVersion } : {}),
            ...(acceptedAt ? { acceptedAt } : {}),
            tcOverlayDismissed: isTcMode ? false : prev.tcOverlayDismissed
        }));

        await handleSaveUserSettings(updates);

        // Both agent paths land on the Dashboard: the welcome tour targets it, and
        // "Skip for now" used to strand agents on Business Partners → Realtors.
        const landing = postOnboardingLanding({ isTcMode, landOnDashboard });
        if (landing.kind === 'tab') {
            setTcOverlayVisible(true);
            navigate(landing.tab);
        } else {
            rrNavigate(landing.path);
        }
    };

    const handleDismissTcModeOverlay = async () => {
        setTcOverlayVisible(false);
        if (userSettings.tcOverlayDismissed) return;
        await handleSaveUserSettings({ tcOverlayDismissed: true });
        setUserSettings(prev => ({ ...prev, tcOverlayDismissed: true }));
    };

    const handleEditClick = (transaction) => {
        setSelectedTransaction(transaction);
        setTransactionFormTab('details');
        rrNavigate(`/transactions/${transaction.id}`);
    };

    const handleOpenFinancials = (transaction) => {
        setSelectedTransaction(transaction);
        if (isTcModeEnabled) {
            showToast('Financial details are not available in TC Mode.', 'info');
            setTransactionFormTab('details');
        } else {
            setTransactionFormTab('financials');
        }
        rrNavigate(`/transactions/${transaction.id}`);
    };

    const handleMoveClick = (transaction) => {
        setTransactionToMove(transaction);
        setMoveTransactionModalOpen(true);
    };

    const navigate = (view) => {
        setSelectedTransaction(null);
        setTransactionFormTab('details');
        rrNavigate(PATH_FOR_VIEW[view] ?? '/');
    };

    const isFormView = currentView === 'form';
    const isTransactionView = !isFormView && currentView === 'transactions';
    // Templates (Email, Checklists, and Action plans tabs) manages its own
    // scroll panes, like the deal form and the transaction list.
    const isTemplateView = currentView === 'templates';
    const isEmailView = isTemplateView;
    const isSettingsView = currentView === 'settings';
    // D-03 (Plan 36-03): tailored Suspense fallback. Dashboard/transactions-list/
    // contacts get the eager RouteSkeleton; every other view (including the
    // Kanban board, which shares currentView === 'transactions' with the list)
    // keeps the existing accent spinner.
    const showSkeleton = SKELETON_VIEWS.has(currentView) && !(currentView === 'transactions' && transactionView === 'kanban');
    const renderedContent = (
        <AppRouter
            whatsNew={{ helpList: announcementHelpList, error: announcementsError, loading: announcementsLoading }}
            workspaces={workspaces}
            user={user}
            selectedWorkspaceId={selectedWorkspaceId}
            selectedWorkspace={selectedWorkspace}
            workspaceDataLoaded={workspaceDataLoaded}
            // null until ensureUserAccount resolves. AppRouter's zero-workspace
            // empty state waits on this: workspaces.length === 0 is briefly
            // true for EVERY brand-new signup while that callable is still in
            // flight, and its "Create Team" button throws in that window.
            accountStatus={accountStatus}
            // DATA-04 (39-14 Task 2 / 39-21 / 39-22 Task 1): AppRouter no
            // longer receives a transactions OR contactsData collection prop
            // under any name -- it leases its own active-window/all-years
            // transactions slices AND its own gated all-contacts slice
            // directly (see AppRouter.js's own destructure comment). App.js's
            // OWN `transactions`/`workspaceContacts` state (from
            // useWorkspaceData's boot listener) is unchanged and still used
            // elsewhere in this file (App.js's own contactsData, handler
            // builders, the selectedTransaction refresh effect) -- only the
            // prop-pass to AppRouter is gone.
            statuses={statuses}
            customStatuses={customStatuses}
            isStatusManagerOpen={isStatusManagerOpen}
            setIsStatusManagerOpen={setIsStatusManagerOpen}
            handleEditClick={handleEditClick}
            handleDeleteTransaction={handleDeleteTransaction}
            handleSetAnniversaryMuted={handleSetAnniversaryMuted}
            handleMoveClick={handleMoveClick}
            isTcModeEnabled={isTcModeEnabled}
            handleOpenFinancials={handleOpenFinancials}
            handleBulkMoveTransactions={handleBulkMoveTransactions}
            handleAddStatus={handleAddStatus}
            handleRenameStatus={handleRenameStatus}
            handleDeleteStatus={handleDeleteStatus}
            handleSetStatusMinimized={handleSetStatusMinimized}
            handleReorderStatus={handleReorderStatus}
            handleMoveStatusBefore={handleMoveStatusBefore}
            handleTransactionDrop={handleTransactionDrop}
            handleCreateIncomeType={handleCreateIncomeType}
            handleUpdateIncomeType={handleUpdateIncomeType}
            handleDeleteIncomeType={handleDeleteIncomeType}
            handleMoveIncomeType={handleMoveIncomeType}
            handleCreateWorkspaceIncome={handleCreateWorkspaceIncome}
            handleUpdateWorkspaceIncome={handleUpdateWorkspaceIncome}
            handleDeleteWorkspaceIncome={handleDeleteWorkspaceIncome}
            setIncomeModalTrigger={setIncomeModalTrigger}
            incomeModalTrigger={incomeModalTrigger}
            handleCreateExpenseType={handleCreateExpenseType}
            handleUpdateExpenseType={handleUpdateExpenseType}
            handleDeleteExpenseType={handleDeleteExpenseType}
            handleMoveExpenseType={handleMoveExpenseType}
            handleCreateWorkspaceExpense={handleCreateWorkspaceExpense}
            handleUpdateWorkspaceExpense={handleUpdateWorkspaceExpense}
            handleUpdateTransactionExpense={handleUpdateTransactionExpense}
            handleDeleteWorkspaceExpense={handleDeleteWorkspaceExpense}
            handleDeleteTransactionExpense={handleDeleteTransactionExpense}
            setExpenseModalTrigger={setExpenseModalTrigger}
            expenseModalTrigger={expenseModalTrigger}
            userSettings={userSettings}
            handleSaveUserSettings={handleSaveUserSettings}
            isSavingSettings={isSavingSettings}
            handleSaveWorkspaceAutomation={handleSaveWorkspaceAutomation}
            isSavingWorkspaceAutomation={isSavingWorkspaceAutomation}
            workspaceDataEntrySettings={workspaceDataEntrySettings}
            handleSaveWorkspaceDefaults={handleSaveWorkspaceDefaults}
            isSavingWorkspaceDefaults={isSavingWorkspaceDefaults}
            isParentWorkspaceOwner={isParentWorkspaceOwner}
            onUpdateChildPolicy={handleUpdateChildPolicy}
            onUpdateChildSplit={handleUpdateChildSplit}
            onRemoveChildWorkspace={handleRemoveTaw}
            onOpenChildWorkspace={handleOpenTaw}
            onRenameChild={handleRenameChild}
            onRemoveChildMember={handleRemoveChildMember}
            patchTaw={patchTaw}
            childWorkspaces={taws}
            teamScope={teamScope}
            onScopeChange={setTeamScope}
            childTransactions={tawTransactions}
            archiveYearFilter={archiveYearFilter}
            onArchiveYearFilterChange={setArchiveYearFilter}
            onAssignTransactionToChild={handleAssignTransactionToChild}
            onReassignTransaction={handleReassignTransaction}
            expModuleSettings={expModuleSettings}
            onSaveExpModuleSettings={handleSaveExpModuleSettings}
            handleUploadWorkspaceLogo={handleUploadWorkspaceLogo}
            handleDeleteWorkspaceLogo={handleDeleteWorkspaceLogo}
            handleUploadWorkspaceDarkLogo={handleUploadWorkspaceDarkLogo}
            handleDeleteWorkspaceDarkLogo={handleDeleteWorkspaceDarkLogo}
            onRequestRenameWorkspace={handleRenameWorkspace}
            onRequestDeleteWorkspace={() => setDeleteWorkspaceModalOpen(true)}
            handleUploadUserHeadshot={handleUploadUserHeadshot}
            handleDeleteUserHeadshot={handleDeleteUserHeadshot}
            handleUploadUserBusinessCard={handleUploadUserBusinessCard}
            handleDeleteUserBusinessCard={handleDeleteUserBusinessCard}
            handleCreateContact={handleCreateContact}
            // Review data WR-05 (D-14): the deal forms' create path, which
            // records consentBasis `deal`.
            handleCreateDealContact={handleCreateDealContact}
            handleUpdateContact={handleUpdateContact}
            handleDeleteWorkspaceContact={handleDeleteWorkspaceContact}
            handleSyncContactFromGoogle={handleSyncContactFromGoogle}
            applyReviewedSync={applyReviewedSync}
            applyResolvedSync={applyResolvedSync}
            lenderHandlers={lenderHandlers}
            titleCompanyHandlers={titleCompanyHandlers}
            realtorHandlers={realtorHandlers}
            companyListHandlers={companyListHandlers}
            vendorModalTrigger={vendorModalTrigger}
            realtorLinkedContactsConfig={realtorLinkedContactsConfig}
            handleCreateChecklistTemplate={handleCreateChecklistTemplate}
            handleUpdateChecklistTemplate={handleUpdateChecklistTemplate}
            handleDeleteChecklistTemplate={handleDeleteChecklistTemplate}
            workspaceMembers={resolvedWorkspaceMembers}
            handleAddTransaction={handleAddTransaction}
            handleCreateTransaction={handleCreateTransaction}
            handleDuplicateTransaction={handleDuplicateTransaction}
            selectedTransaction={selectedTransaction}
            handleUpdateTransaction={handleUpdateTransaction}
            handleSearchGoogleContacts={handleSearchGoogleContacts}
            transactionFormTab={transactionFormTab}
            showToast={showToast}
        />
    );
    // Full-height, self-managed scroll pages (transaction list/form, template
    // editors, settings) own their own bottom spacing and scroll internally.
    // The shell must NOT add a bottom padding for them — with the permanent
    // footer gone, that padding left a dead strip below the scroll pane so
    // content stopped short of the window bottom. Naturally-scrolling pages
    // keep their trailing padding (it reads as scroll breathing room).
    const mainClassName = isMobileViewport
        ? (isFormView || isTransactionView
            ? 'flex-1 min-h-0 px-4 overflow-hidden flex flex-col'
            : isTemplateView
                ? 'flex-1 min-h-0 overflow-hidden px-4 pt-0 flex flex-col'
                : isSettingsView
                    ? 'flex-1 min-h-0 overflow-y-auto px-4 pt-4'
                    : `flex-1 min-h-0 overflow-y-auto px-4 pb-8 ${isEmailView ? 'pt-0' : 'pt-4'}`)
        : ((isFormView || isTransactionView)
            ? 'flex-1 min-h-0 px-6 overflow-hidden flex flex-col'
            : (isTemplateView
                ? 'flex-1 min-h-0 overflow-hidden px-6 pt-0 flex flex-col'
                : isSettingsView
                    ? 'flex-1 min-h-0 overflow-y-auto px-6 pt-6'
                    : `flex-1 min-h-0 overflow-y-auto px-6 pb-6 ${isEmailView ? 'pt-0' : 'pt-6'}`));
    // Naturally-scrolling pages (clients, income, expenses, dashboard, settings,
    // etc.) share the overflow-y-auto <main> branches above; forms/transactions/
    // templates manage their own internal scroll and stay overflow-hidden. This
    // flag drives the data-scroll-root handle below so a route page (e.g.
    // ClientsV2Page) can resolve this <main> as a react-virtuoso customScrollParent
    // without threading a ref through the AppRouter prop chain (Phase 37 Plan 04).
    const isNaturallyScrollingMain = !(isFormView || isTransactionView || isTemplateView);
    const headerTitle = getViewTitle(currentView, {
        transactionView,
        transactionFormTab,
        selectedTransaction
    });
    // D-10/51-08: third sibling after AccountStatusBanner, same flex column,
    // same no-height-opinion shape. Built once here (rather than inlined
    // twice below) so the mobile suspended-workspace collapse wrapper can
    // wrap this single element without a second, divergent JSX literal.
    const billingLifecycleStripElement = (
        <BillingLifecycleStrip
            workspaceId={selectedWorkspaceId}
            billing={selectedWorkspace?.billing}
            isOwner={canManageWorkspaceAccess}
            workspaceStatus={selectedWorkspace?.status}
        />
    );
    // D-15/D-18/UI-SPEC: the pop-up never shows over the onboarding wizard or
    // a running tour. showOnboardingWizard is the same flag that gates
    // OnboardingWizard's own render below. isTourOverlayActive is React state
    // from tourRunningStore, which TourContext's launcher sets around
    // driver.js's drive() and destroy hooks (B-WR-02). App renders
    // TourProvider, so it reads the module store, not the context. A pop-up
    // suppressed by a tour appears once the tour ends.
    const showAnnouncementPopup = Boolean(user)
        && Boolean(selectedWorkspaceId)
        && popupAnnouncements.length > 0
        && !showOnboardingWizard
        && !isTourOverlayActive;
    const appShell = (
        <TourProvider
            userId={user?.uid}
            tourState={userSettings.tourState}
            onTourStateUpdate={(payload) => workspaceService.saveUserSettings(user.uid, payload)}
            navigateToRoute={rrNavigate}
        >
        <KeyFlowAiProvider>
        <BrokerageProvider value={brokerageContextValue}>
        <div className={`app-shell ${isMobileViewport ? 'app-shell--mobile' : ''} h-[100dvh] lg:h-screen font-sans flex flex-col lg:flex-row overflow-hidden`}>
            <Sidebar
                isCollapsed={isSidebarCollapsed}
                setIsCollapsed={setIsSidebarCollapsed}
                isMobile={isMobileViewport}
                isOpen={isMobileNavOpen}
                onClose={() => setIsMobileNavOpen(false)}
                canAccessManageWorkspace={canAccessManageWorkspace}
                isViewingChildAsParent={isViewingChildAsParent}
                parentWorkspaceName={tw?.name || ''}
                childWorkspaceName={selectedWorkspace?.name || ''}
                onBackToParent={isViewingChildAsParent ? handleBackToParent : null}
                workspaces={workspaces}
                selectedWorkspaceId={selectedWorkspaceId}
                onSelectWorkspace={setSelectedWorkspaceId}
                onOpenWhatsNew={handleOpenWhatsNew}
            />
            <MobileHeaderActionsProvider>
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {isMobileViewport && !isFormView && (
                    <header className="mobile-app-header">
                        <div className="mobile-app-header__top">
                            <button
                                type="button"
                                onClick={() => setIsMobileNavOpen(true)}
                                className="mobile-app-header__menu"
                                aria-label="Open navigation"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="sidebar__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                            <div className="mobile-app-header__title-group">
                                <span className="mobile-app-header__eyebrow">KeyFlow</span>
                                <h1 className="mobile-app-header__title">{headerTitle}</h1>
                            </div>
                            <MobileHeaderActionsSlot />
                        </div>
                    </header>
                )}
                <VersionBanner />
                <MessagingOnboardingBanner workspace={selectedWorkspace} currentUserUid={user?.uid} />
                <AccountStatusBanner workspaceStatus={selectedWorkspace?.status} isCascaded={isAccountStatusCascaded} statusReason={accountStatusReason} />
                {selectedWorkspace?.status === 'suspended'
                    // Below 640px, AccountStatusBanner's uncapped prose plus this
                    // strip would trade directly against <main>'s scrollable
                    // height on a definite, non-growing viewport (the
                    // fixed-viewport model: body overflow hidden, app-shell at a
                    // real height, never a min-height substitute). This only
                    // ever suppresses the billing strip, never the suspension
                    // banner itself -- that fact stays visible at every width.
                    // The suppressed information stays reachable at any width
                    // via Workspace -> Billing.
                    ? <div className="hidden sm:block">{billingLifecycleStripElement}</div>
                    : billingLifecycleStripElement}
                <ContractExtractionNotifier workspaceId={selectedWorkspaceId} />
                <main className={mainClassName} style={isMobileViewport ? undefined : { paddingRight: 'calc(1.5rem + var(--ai-dock-width, 0px))' }} {...(isNaturallyScrollingMain ? { 'data-scroll-root': true } : {})}>
                    <RouteChunkErrorBoundary>
                        <React.Suspense fallback={showSkeleton ? <RouteSkeleton view={currentView} /> : <div className="flex-1 flex items-center justify-center p-10"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent" /></div>}>
                            {(isFormView || isTransactionView) ? (
                                <div className="flex-1 min-h-0">
                                    {renderedContent}
                                </div>
                            ) : renderedContent}
                        </React.Suspense>
                    </RouteChunkErrorBoundary>
                </main>
            </div>
            </MobileHeaderActionsProvider>
            {showOnboardingWizard && user && (
                <OnboardingWizard
                    initialFirstName={wizardInitialNames.firstName}
                    initialLastName={wizardInitialNames.lastName}
                    user={user}
                    workspace={selectedWorkspace}
                    selectedWorkspaceId={selectedWorkspaceId}
                    onComplete={handleCompleteOnboarding}
                />
            )}
            <TcModeOverlay isOpen={isTcOverlayVisible} onClose={handleDismissTcModeOverlay} />
            {/* Persistent Help surface (D-01): bottom-right speed-dial FAB + right slide-over drawer.
                HelpFab replaces the former FloatingHelpButton (top-right) and Sentry's autoInject
                widget so both launchers are unified in one affordance. */}
            <AppNudge />
            {/* Reads the one-shot flag WelcomePage.js sets before navigating here
                after a platform invitation is accepted, and launches the welcome
                tour exactly once. See WelcomeTourLauncher.js's header comment. */}
            <WelcomeTourLauncher />
            {/* Consumes the article -> tour launch intent (?launchTour=<id>) that
                HelpArticlePage.js's "Take the interactive tour" CTA sets before
                navigating here (45-CONTEXT.md D-05). See TourLaunchIntent.js's
                header comment. */}
            <TourLaunchIntent />
            {/* "What's New" pop-up (D-14, D-15, D-18): stacked, shows once
                per unseen announcement, gated above. */}
            {showAnnouncementPopup && (
                <AnnouncementPopup announcements={popupAnnouncements} onDismiss={markAnnouncementsShown} />
            )}
            <KeyFlowAiSurfaces
                user={user}
                workspaceId={selectedWorkspaceId}
                workspace={selectedWorkspace}
                hasUnseen={hasUnseenAnnouncements}
                openHelpRef={openHelpRef}
                restartOnboarding={handleRestartOnboarding}
                hasWorkspace={Boolean(selectedWorkspaceId)}
                helpFocusWhatsNew={helpFocusWhatsNew}
                onFocusWhatsNewHandled={() => setHelpFocusWhatsNew(false)}
                onSeeAllUpdates={() => requestNavigate(PATH_FOR_VIEW.whatsNew, () => rrNavigate(PATH_FOR_VIEW.whatsNew))}
                navigateTo={(path) => requestNavigate(path, () => rrNavigate(path))}
                onDashboard={currentView === 'dashboard'}
                dealWriters={dealWriters}
                whatsNew={{
                    helpList: announcementHelpList,
                    helpPanel: announcementHelpPanel,
                    hasUnseen: hasUnseenAnnouncements,
                    error: announcementsError,
                    loading: announcementsLoading,
                }}
            />
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
            {isCreateWorkspaceModalOpen && <CreateWorkspaceModal onClose={() => setCreateWorkspaceModalOpen(false)} onCreate={handleCreateWorkspace} />}
            {isDeleteWorkspaceModalOpen && selectedWorkspace && (() => {
                const blockers = deleteBlockers({ workspace: selectedWorkspace, workspaces: allWorkspaces || workspaces });
                return (
                    <DeleteWorkspaceModal
                        workspaceName={selectedWorkspace.name}
                        onClose={() => setDeleteWorkspaceModalOpen(false)}
                        onDelete={handleDeleteWorkspace}
                        childWorkspaces={blockers.childWorkspaces}
                        hasLiveSubscription={blockers.reasons.includes('active_subscription')}
                        onGoToBilling={() => { setDeleteWorkspaceModalOpen(false); rrNavigate(`${PATH_FOR_VIEW.manageWorkspace}?tab=billing`); }}
                        onOpenTeamMembers={() => { setDeleteWorkspaceModalOpen(false); rrNavigate(`${PATH_FOR_VIEW.manageWorkspace}?tab=taws`); }}
                    />
                );
            })()}
            {isMoveTransactionModalOpen && transactionToMove && <MoveTransactionModal workspaces={workspaces.filter(ws => ws.id !== selectedWorkspaceId)} onClose={() => setMoveTransactionModalOpen(false)} onMove={handleMoveTransaction} />}
        </div>
        </BrokerageProvider>
        </KeyFlowAiProvider>
        </TourProvider>
    );

    return (
        <AuthProvider value={authContextValue}>
            {versionModeForPath(location.pathname) === 'quiet' && (
                <QuietReloadWatcher reloadOnClientTooOld={!location.pathname.toLowerCase().startsWith('/portal')} />
            )}
            <Routes>
                {/* Every path here comes from PUBLIC_ROUTE_PATHS, which is also
                    what gates the ensureUserAccount effect above. One list, so
                    the gate cannot drift away from the route table -- it had
                    already lost /portal/:token when it was a separate Set. */}
                <Route path={PUBLIC_ROUTE_PATHS.portal} element={<ClientPortalRouteWrapper />} />
                {/* Phase 68 (D-14): public lead pages render before the shell, so a
                    signed-in agent who opens a lead link never provisions a workspace
                    and a guest never sees CRM UI. */}
                <Route path={PUBLIC_ROUTE_PATHS.lead} element={<React.Suspense fallback={<PublicRouteFallback />}><LeadFormRoute /></React.Suspense>} />
                <Route path={PUBLIC_ROUTE_PATHS.openHouse} element={<React.Suspense fallback={<PublicRouteFallback />}><OpenHouseRoute /></React.Suspense>} />
                <Route path={PUBLIC_ROUTE_PATHS.kiosk} element={<React.Suspense fallback={<PublicRouteFallback />}><KioskRoute /></React.Suspense>} />
                <Route path={PUBLIC_ROUTE_PATHS.confirmLead} element={<React.Suspense fallback={<PublicRouteFallback />}><ConfirmRoute /></React.Suspense>} />
                <Route path={PUBLIC_ROUTE_PATHS.login} element={<Login onSignIn={signInWithGoogle} />} />
                <Route path={PUBLIC_ROUTE_PATHS.privacy} element={<div className="h-screen overflow-y-auto bg-[var(--color-light)]"><PrivacyPolicy onBack={() => rrNavigate(-1)} /></div>} />
                <Route path={PUBLIC_ROUTE_PATHS.terms} element={<div className="h-screen overflow-y-auto bg-[var(--color-light)]"><TermsOfService onBack={() => rrNavigate(-1)} /></div>} />
                {/* D-02/D-04 (50-CONTEXT.md): the public plan-selection mount.
                    h-screen plus its own overflow-y-auto is a DEFINITE-height
                    scroll container -- a minimum-height wrapper would render
                    correctly at first paint and then be unable to scroll at
                    all once the grid exceeds the viewport, per this contract's
                    Fixed-Viewport rule; see PlanSelection.js's own header
                    comment for the reasoning.
                    Deliberately --body-bg here, not the older --color-light
                    literal the /privacy and /terms rows above still carry --
                    this phase's UI contract specifies --body-bg and that older
                    token is a pre-existing inconsistency, not this phase's to
                    fix. D-04: a signed-in user who already has a workspace is
                    redirected into it via a route-level guard rather than a
                    PlanSelection render branch -- Phase 52 owns plan change,
                    and a second path into Checkout for an existing customer is
                    how duplicate subscriptions get created. SignupRedirect
                    keeps a `?promo=` code by sending a Free owner to the
                    upgrade modal instead of `/`. */}
                <Route path={PUBLIC_ROUTE_PATHS.signup} element={(user && workspaces.length > 0)
                    ? <SignupRedirect />
                    : (
                        <div className="h-screen overflow-y-auto bg-[color:var(--body-bg)]">
                            <PlanSelection chrome="standalone" />
                        </div>
                    )
                } />
                <Route path={PUBLIC_ROUTE_PATHS.acceptInvite} element={<AcceptInvitationPage />} />
                <Route path={PUBLIC_ROUTE_PATHS.welcome} element={<WelcomePage />} />
                {/* 44-08 (D-01, D-02): /help and /help/:slug are public, chrome-owned
                    routes -- HelpChrome (not appShell) owns their layout and scroll
                    container, so neither wraps in the h-screen overflow-y-auto div the
                    /privacy and /terms rows use. Each gets its own NEW chunk-load
                    error-boundary + Suspense instance below: the only other usage of
                    that boundary in this file sits inside appShell, under
                    ProtectedRoute, so this is a second and third instantiation of the
                    pattern, not a reuse of that wrapped branch -- do not "deduplicate"
                    the three into one, which would move these public routes under the
                    auth gate. */}
                <Route path={PUBLIC_ROUTE_PATHS.help} element={(
                    <RouteChunkErrorBoundary>
                        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent" /></div>}>
                            <HelpIndexPage />
                        </React.Suspense>
                    </RouteChunkErrorBoundary>
                )} />
                <Route path={PUBLIC_ROUTE_PATHS.helpArticle} element={(
                    <RouteChunkErrorBoundary>
                        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent" /></div>}>
                            <HelpArticlePage />
                        </React.Suspense>
                    </RouteChunkErrorBoundary>
                )} />
                <Route element={<ProtectedRoute user={user} />}>
                    {/* Task 7 (D-10): a deleted account's workspaces are unreadable
                        under the rules layer (Task 3), so this gate must render
                        BEFORE the Sidebar's workspace switcher — otherwise a
                        deleted user lands in empty-state onboarding instead of
                        being told their account is deleted. */}
                    <Route path="*" element={(accountStatus === 'deleted' || accountCascadeDeleted)
                        ? (
                            <AccountDeletedScreen
                                purgeAfter={accountPurgeAfter}
                                statusReason={accountStatusReason}
                                isCascaded={accountCascadeDeleted}
                            />
                        )
                        : appShell} />
                </Route>
            </Routes>
        </AuthProvider>
    );
}

export default App;
